import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import axios from "axios";
import QRCode from "qrcode";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL, QR_ENDPOINTS } from "../config";
import defaultAvatar from "../assets/default-avatar.png";
import useToast from "../hooks/useToast";
import WalletModal from "../components/WalletModal";
import editIcon from '../assets/Edit.svg';

// "Lavender Haze" light theme — same source of truth as ChatPage. Light-mode
// surfaces, lines and accents are pulled from `LH.profile` so the profile and
// chat pages share one palette and stay visually consistent.
import { LH, LAVENDER_HAZE } from "../styles/chatPageLightTheme";

// ── Local Lavender-Haze fallback for the profile page ─────────────────────────
// Defensive copy of `LH.profile`. If `chatPageLightTheme.js` in this build
// predates the `profile` block (e.g. partial deploy / stale chunk / older
// branch), we still want the page to render with the right colors instead
// of crashing on `LH_PROFILE.themeVars`. The inline values are identical to
// the ones in the shared theme file — keep them in sync.
const _LH_PROFILE_FALLBACK = {
  cssVar: {
    bg:           "#ece5f8",
    text:         "#1d143f",
    heading:      "#1d143f",
    soft:         "#5e5180",
    muted:        "#8a7eaa",
    card:         "rgba(250, 247, 255, .82)",
    cardStrong:   "rgba(250, 247, 255, .94)",
    cardNested:   "rgba(243, 238, 251, .78)",
    line:         "rgba(80, 50, 160, 0.14)",
    lineStrong:   "rgba(80, 50, 160, 0.22)",
    accent:       "#6d4adb",
    accentSoft:   "rgba(109, 74, 219, .12)",
    accentLine:   "rgba(109, 74, 219, .24)",
    shadow:       "0 18px 44px rgba(80, 50, 160, .12)",
    scrollShadow: "rgba(80, 50, 160, .22)",
  },
  pageBackground:
    "radial-gradient(ellipse 46% 38% at 14% 0%, rgba(109, 74, 219, 0.18), transparent 68%), " +
    "radial-gradient(ellipse 50% 42% at 90% 4%, rgba(168, 140, 235, 0.16), transparent 66%), " +
    "radial-gradient(ellipse 42% 36% at 76% 96%, rgba(168, 85, 247, 0.12), transparent 68%), " +
    "linear-gradient(180deg, #ece5f8 0%, #e3d7f1 56%, #ddd1f0 100%)",
  themeVars: {
    bg:         "#ece5f8",
    fg:         "#1d143f",
    card:       "rgba(250, 247, 255, 0.82)",
    border:     "#6d4adb",
    btnBg:      "linear-gradient(135deg, #6d4adb 0%, #5a3bc8 100%)",
    btnFg:      "#ffffff",
    success:    "#0abf30",
    warning:    "#a3590f",
    error:      "#f24d4c",
    disabledBg: "#c5b6ec",
    gradient:   "linear-gradient(135deg, #6d4adb 0%, #5a3bc8 100%)",
  },
  // Warning banner tokens — deep amber + tinted bg readable on lavender shell
  warningText:    "#a3590f",
  warningBg:      "rgba(217, 119, 6, 0.12)",
  warningBorder:  "rgba(217, 119, 6, 0.45)",
  // Stronger variants for interactive amber elements inside warning surfaces
  // (CTAs, code-input borders) — slightly more opaque so they read as "above"
  // the banner background.
  warningBtnBg:     "rgba(217, 119, 6, 0.20)",
  warningBtnBorder: "rgba(217, 119, 6, 0.55)",
  balanceLabel:        "rgba(94, 81, 128, .72)",
  balanceDot:          "rgba(94, 81, 128, .45)",
  bannerInputBg:       "rgba(250, 247, 255, .78)",
  statHoverBorder:     "rgba(109, 74, 219, .32)",
  statHoverBg:         "rgba(250, 247, 255, .92)",
  matchTabActive:      "linear-gradient(135deg, #6d4adb, #5a3bc8)",
  matchTabText:        "#ffffff",
  matchButtonBg:       "rgba(109, 74, 219, .08)",
  navInputBg:          "rgba(250, 247, 255, .72)",
  toggleBg:            "#ddd1f0",
  eventHistoryBadge:   "rgba(94, 81, 128, .12)",
  qrShadow:            "0 12px 28px rgba(80, 50, 160, .18)",
  dashedBorder:        "rgba(80, 50, 160, .22)",
  modalBg:             "#faf7ff",
  modalBorder:         "rgba(80, 50, 160, .14)",
  modalSubtleBg:       "rgba(109, 74, 219, .05)",
  modalSubtleBorder:   "rgba(80, 50, 160, .10)",
  modalCloseBg:        "rgba(109, 74, 219, .07)",
  emailInputBg:        "rgba(250, 247, 255, .82)",
};

const LH_PROFILE = (LH && LH.profile) ? LH.profile : _LH_PROFILE_FALLBACK;

// ── Profile cache (stale-while-revalidate, no cookies) ────────────────────────
const CACHE_KEYS = {
  PROFILE_INFO:     "swr_profileInfo",
  CATEGORIES:       "swr_categories",
  MATCHES:          "swr_matchesByCategory",
  MATCHING_STATUS:  "swr_matchingStatus",
  CATEGORY_PARAMS:  "swr_categoryParams",
  EVENT_REGS:       "swr_eventRegistrations",
  SEARCH_REQUESTS:  "swr_searchRequestsByCategory",
  BALANCE:          "swr_balance",
  INVITED_USERS:    "swr_invitedUsers",
};

const INVITES_TIME_FILTER_KEY = "profileInvitesTimeFilter";
const INVITES_TIME_FILTERS = ["all", "week", "month", "year"];

function readInvitesTimeFilter() {
  try {
    const saved = localStorage.getItem(INVITES_TIME_FILTER_KEY);
    return INVITES_TIME_FILTERS.includes(saved) ? saved : "week";
  } catch {
    return "week";
  }
}

/**
 * Preserve sorted order from `sortDashboardMatches` (pending → newest).
 * Reassigns `pageIndex` per `id` so the N/M badge matches the natural order.
 */
function finalizeDashboardMatchPageOrder(pages) {
  const perIdNext = new Map();
  return pages.map((p) => {
    const pageIndex = perIdNext.get(p.id) ?? 0;
    perIdNext.set(p.id, pageIndex + 1);
    return { ...p, pageIndex };
  });
}

function readCache(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn("Cache write failed:", key, e);
  }
}

function clearProfileCache() {
  Object.values(CACHE_KEYS).forEach((k) => {
    try { localStorage.removeItem(k); } catch {}
  });
}
// ──────────────────────────────────────────────────────────────────────────────

// Category metadata – defined once outside the component
const CATEGORY_META = [
  { key: "clients",  backendKey: "business_clients", en: "Clients",  fr: "Clients",  color: "#667eea" },
  { key: "startup",  backendKey: "startup",          en: "Startup",  fr: "Startup",  color: "#764ba2" },
  { key: "job",      backendKey: "job",              en: "Job",      fr: "Emploi",   color: "#0abf30" },
  { key: "other",    backendKey: "other",            en: "Other",    fr: "Autre",    color: "#e9bd0c" },
  { key: "friends",  backendKey: "friends",          en: "Friends",  fr: "Amis",     color: "#f97316" },
];

/**
 * Match list wheel paginator — MUST stay at module scope.
 * Defining this inside ProfilePage’s render/IIFE creates a new component type every render,
 * so React unmounts/remounts it constantly: physics reset, native wheel listeners lost, layout glitches.
 */
const WHEEL_HOVER_BG = "rgb(102, 126, 234)";
/** Type scale (px): inactive → +1 selected → +1 more when hovered/pressed on top of selection */
const WHEEL_FS = { base: 13, selected: 14, selectedHot: 15 };

/** Shared timing: history cards + wheel strip move together (snappy, not sluggish). */
const HISTORY_TRANSITION_SEC = 0.48;
const HISTORY_TRANSITION_EASE = [0.25, 1, 0.32, 1];
/** Mobile “Match by demand” carousel: one fixed-height viewport; content scrolls inside (slider, not jumping windows). */
const MOBILE_MATCH_HISTORY_VIEWPORT_H = "min(56dvh, 460px)";
/** Touches starting here must not trigger matches-block horizontal swipe (lets nested scroll work). */
const MATCH_BLOCK_TOUCH_SCROLL_ROOT =
  "[data-matches-carousel-scroll],[data-matches-scroll-viewport]";
/** Match reason scroll cap: `.match-reason-outer-clip` / `.match-reason-scroll-h` in page `<style>` (320px + min(40vh,320px) when supported). */

/**
 * Прошло ли матчинговое окно ивента. Источник истины — флаг `matching_expired`
 * с бека (см. /events/my_registrations). Если бек его не вернул (старый кэш) —
 * считаем локально: matching_start_after, иначе event_date. Сравниваем по дате
 * без времени, чтобы не зависеть от таймзоны клиента.
 */
function isEventMatchingExpired(eventItem) {
  if (!eventItem) return false;
  if (typeof eventItem.matching_expired === "boolean") return eventItem.matching_expired;
  const cutoffStr = eventItem.matching_start_after || eventItem.event_date;
  if (!cutoffStr) return false;
  const cutoff = new Date(cutoffStr);
  if (Number.isNaN(cutoff.getTime())) return false;
  const todayKey = new Date();
  todayKey.setHours(0, 0, 0, 0);
  cutoff.setHours(0, 0, 0, 0);
  return todayKey.getTime() > cutoff.getTime();
}

/** Inset edge fades on `.neo-request-match-reason`: bottom when more text below, top when more above. */
function syncMatchReasonScrollEdges(el) {
  if (!el) return;
  const fuzz = 6;
  const { scrollTop, scrollHeight, clientHeight } = el;
  const overflow = scrollHeight - clientHeight;
  if (overflow <= fuzz) {
    el.classList.remove("neo-request-match-reason--fade-top", "neo-request-match-reason--fade-bottom");
    return;
  }
  const atTop = scrollTop <= fuzz;
  const atBottom = scrollTop + clientHeight >= scrollHeight - fuzz;
  el.classList.toggle("neo-request-match-reason--fade-top", !atTop);
  el.classList.toggle("neo-request-match-reason--fade-bottom", !atBottom);
}

/**
 * Match page transitions — horizontal, aligned with wheel (1→2→3 left→right):
 * forward (higher page #) slides in from the right, outgoing exits left; back is the inverse.
 * `mode="wait"` avoids overlapping slides (no ghosting after page scroll / popLayout stacking).
 * `contentViewportHeight`: when set, outer box is a fixed-height viewport with internal scroll.
 */
function MatchesCarouselSlide({ pageKey, direction, children, contentViewportHeight }) {
  const reduceMotion = useReducedMotion();
  const fixed = !!contentViewportHeight;
  /** Framer `custom` must stay stable while `pageKey` is unchanged — re-renders were passing direction=0 and breaking exit/enter after clicks. */
  const carouselKeyRef = useRef(null);
  const carouselDirRef = useRef(0);
  if (carouselKeyRef.current !== pageKey) {
    carouselDirRef.current = direction;
    carouselKeyRef.current = pageKey;
  }
  const dir = carouselDirRef.current === 0 ? 0 : carouselDirRef.current > 0 ? 1 : -1;

  if (reduceMotion) {
    return (
      <div
        key={pageKey}
        style={{
          width: "100%",
          ...(fixed
            ? {
                height: contentViewportHeight,
                minHeight: contentViewportHeight,
                maxHeight: contentViewportHeight,
                overflow: "hidden",
                boxSizing: "border-box",
              }
            : {}),
        }}
      >
        {fixed ? (
          <div
            data-matches-carousel-scroll
            style={{
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        isolation: "isolate",
        ...(fixed
          ? {
              height: contentViewportHeight,
              minHeight: contentViewportHeight,
              maxHeight: contentViewportHeight,
              boxSizing: "border-box",
            }
          : {}),
      }}
    >
      <AnimatePresence mode="wait" initial={false} custom={dir}>
        <motion.div
          key={pageKey}
          custom={dir}
          role="presentation"
          variants={{
            enter: (d) =>
              d === 0 ? { x: 0 } : { x: d > 0 ? "100%" : "-100%" },
            center: { x: 0 },
            exit: (d) =>
              d === 0 ? { x: 0 } : { x: d > 0 ? "-100%" : "100%" },
          }}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            duration: HISTORY_TRANSITION_SEC,
            ease: HISTORY_TRANSITION_EASE,
          }}
          style={{
            width: "100%",
            height: fixed ? "100%" : undefined,
            boxSizing: "border-box",
            display: fixed ? "flex" : undefined,
            flexDirection: fixed ? "column" : undefined,
            overflow: fixed ? "hidden" : undefined,
            position: "relative",
            zIndex: 1,
          }}
        >
          {fixed ? (
            <div
              data-matches-carousel-scroll
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {children}
            </div>
          ) : (
            children
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function MatchesWheelPagination({
  currentPage,
  totalPages,
  setPage,
  theme,
  themeVars,
  isSamsungBrowser,
}) {
  const reduceMotion = useReducedMotion();
  const [hoveredPage, setHoveredPage] = useState(null);
  const [pressedPage, setPressedPage] = useState(null);
  /** Page index under the center notch (tracks drag + fling; matches highlight colors). */
  const [liveCenter, setLiveCenter] = useState(currentPage);
  const wheelRef = useRef(null);
  const innerRef = useRef(null);
  const stripParallaxRef = useRef(null);
  /** After first sync from props, animate further page changes (matches carousel timing). */
  const hasSyncedFromPropsRef = useRef(false);
  const totalPagesRef = useRef(totalPages);
  totalPagesRef.current = totalPages;
  const sRef = useRef({
    offset: 0,
    velocity: 0,
    dragging: false,
    lastX: 0,
    lastTime: 0,
    animId: null,
    settled: true,
    pageSet: currentPage,
  });

  const ITEM_W = 46;
  const VISIBLE = 7;
  const WHEEL_W = ITEM_W * VISIBLE;
  const accent = themeVars.border;
  const fg = themeVars.fg;
  const isD = theme === "dark" || isSamsungBrowser;
  const FRICTION = 0.88;
  const SNAP_STRENGTH = 0.22;
  const MIN_VEL = 0.45;
  /** Mouse / trackpad wheel: short cooldown keeps rapid scroll responsive without runaway pages. */
  const WHEEL_STEP_COOLDOWN_MS = 200;
  const wheelStepLockRef = useRef(0);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const pageToOffset = (pg) => -(pg - 1) * ITEM_W;
  const offsetToPage = (off, tp = totalPages) =>
    Math.max(1, Math.min(tp, Math.round(-off / ITEM_W) + 1));

  const applyTransformImmediate = (off) => {
    const par = off * 0.6;
    if (innerRef.current) {
      innerRef.current.style.transition = "none";
      innerRef.current.style.transform = `translateX(${off}px)`;
    }
    if (stripParallaxRef.current) {
      stripParallaxRef.current.style.transition = "none";
      stripParallaxRef.current.style.transform = `translateX(${par}px)`;
    }
    if (wheelRef.current) wheelRef.current.style.setProperty("--tx", `${par}px`);
    const s = sRef.current;
    if (s.dragging || !s.settled) {
      const tp = totalPagesRef.current;
      const p = offsetToPage(off, tp);
      setLiveCenter((prev) => (prev !== p ? p : prev));
    }
  };

  const applyTransformSmoothTo = (to) => {
    const par = to * 0.6;
    const ease = `cubic-bezier(${HISTORY_TRANSITION_EASE.join(",")})`;
    const trans = `transform ${HISTORY_TRANSITION_SEC}s ${ease}`;
    if (innerRef.current) {
      innerRef.current.style.transition = trans;
      innerRef.current.style.transform = `translateX(${to}px)`;
    }
    if (stripParallaxRef.current) {
      stripParallaxRef.current.style.transition = trans;
      stripParallaxRef.current.style.transform = `translateX(${par}px)`;
    }
    if (wheelRef.current) wheelRef.current.style.setProperty("--tx", `${par}px`);
  };

  // Physics loop: never restart on `currentPage` change — avoids jank / dropped frames on fast paging.
  useEffect(() => {
    let running = true;
    const tick = () => {
      if (!running) return;
      const s = sRef.current;
      const tp = totalPagesRef.current;
      if (!s.dragging && !s.settled) {
        s.velocity *= FRICTION;
        const snapOff = pageToOffset(offsetToPage(s.offset, tp));
        const diff = snapOff - s.offset;
        s.velocity += diff * SNAP_STRENGTH;
        s.offset += s.velocity;

        const minOff = pageToOffset(tp);
        const maxOff = pageToOffset(1);
        if (s.offset < minOff) {
          s.offset = minOff;
          s.velocity = 0;
        }
        if (s.offset > maxOff) {
          s.offset = maxOff;
          s.velocity = 0;
        }

        applyTransformImmediate(s.offset);

        if (Math.abs(s.velocity) < MIN_VEL && Math.abs(diff) < 0.5) {
          s.offset = snapOff;
          s.velocity = 0;
          s.settled = true;
          applyTransformImmediate(s.offset);
          const pg = offsetToPage(s.offset, tp);
          setLiveCenter(pg);
          if (pg !== s.pageSet && pg >= 1 && pg <= tp) {
            s.pageSet = pg;
            setPage(pg);
          }
        }
      }
      s.animId = requestAnimationFrame(tick);
    };
    sRef.current.animId = requestAnimationFrame(tick);
    return () => {
      running = false;
      const id = sRef.current.animId;
      if (id != null) cancelAnimationFrame(id);
    };
  }, [setPage]);

  useLayoutEffect(() => {
    const s = sRef.current;
    if (s.dragging) return;

    const target = pageToOffset(currentPage);
    const prevOff = s.offset;
    s.pageSet = currentPage;
    s.velocity = 0;
    s.settled = true;
    setLiveCenter(currentPage);
    const moved = Math.abs(prevOff - target) > 0.5;
    const shouldSmooth = hasSyncedFromPropsRef.current && !reduceMotion && moved;
    hasSyncedFromPropsRef.current = true;
    s.offset = target;

    if (shouldSmooth) {
      applyTransformImmediate(prevOff);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          applyTransformSmoothTo(target);
        });
      });
    } else {
      applyTransformImmediate(target);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- imperative wheel DOM; deps are currentPage/totalPages/reduceMotion only
  }, [currentPage, totalPages, reduceMotion]);

  useEffect(() => {
    setHoveredPage(null);
    setPressedPage(null);
  }, [currentPage, totalPages]);

  useLayoutEffect(() => {
    const el = wheelRef.current;
    if (!el) return;
    const onWheelNative = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const tp = totalPagesRef.current;
      if (tp <= 1) return;
      const now = Date.now();
      if (now - wheelStepLockRef.current < WHEEL_STEP_COOLDOWN_MS) return;
      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (Math.abs(delta) < 0.5) return;
      wheelStepLockRef.current = now;
      const cp = currentPageRef.current;
      const next = delta > 0 ? Math.min(tp, cp + 1) : Math.max(1, cp - 1);
      if (next !== cp) setPage(next);
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, [setPage]);

  const onPointerDown = (e) => {
    // Clicks on page numbers must not capture the pointer — otherwise click never reaches the slot (see onClick).
    if (e.target.closest("[data-wheel-page-num]")) return;
    const s = sRef.current;
    applyTransformImmediate(s.offset);
    s.dragging = true;
    s.settled = false;
    s.lastX = e.clientX;
    s.lastTime = Date.now();
    s.velocity = 0;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    const s = sRef.current;
    if (!s.dragging) return;
    const now = Date.now();
    const dx = e.clientX - s.lastX;
    const dt = Math.max(1, now - s.lastTime);
    s.lastX = e.clientX;
    s.lastTime = now;
    s.offset += dx;
    s.velocity = dx * (16 / dt);
    applyTransformImmediate(s.offset);
  };

  const finishPointerDrag = (e) => {
    const s = sRef.current;
    const el = wheelRef.current;
    if (s.dragging && el != null && e?.pointerId != null) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }
    s.dragging = false;
    if (s.settled) return;
    const tp = totalPagesRef.current;
    // Fast snap when the user isn’t flinging — avoids “dead” frames before physics settles.
    if (Math.abs(s.velocity) < 14) {
      const pg = offsetToPage(s.offset, tp);
      const snapOff = pageToOffset(pg);
      s.offset = snapOff;
      s.velocity = 0;
      s.settled = true;
      applyTransformImmediate(snapOff);
      setLiveCenter(pg);
      if (pg !== currentPageRef.current) {
        s.pageSet = pg;
        setPage(pg);
      }
    }
  };

  const onPointerUp = (e) => {
    finishPointerDrag(e);
  };

  const onPointerCancel = (e) => {
    finishPointerDrag(e);
  };

  const onPageClick = (pg) => {
    if (pg < 1 || pg > totalPages || pg === currentPage) return;
    setPage(pg);
  };

  const BUF = 3;
  const allItems = [];
  for (let i = 1 - BUF; i <= totalPages + BUF; i++) {
    allItems.push(i);
  }
  const stripLeft = 0;

  const notchBg = isD
    ? `repeating-linear-gradient(to right, ${accent}55 0px, ${accent}55 3px, transparent 3px, transparent 7px)`
    : `repeating-linear-gradient(to right, ${accent}44 0px, ${accent}44 3px, transparent 3px, transparent 7px)`;

  const highlightPage = hoveredPage ?? pressedPage;
  const isLifted = highlightPage != null;

  useEffect(() => {
    if (pressedPage === null) return;
    const clear = () => setPressedPage(null);
    window.addEventListener("pointerup", clear);
    window.addEventListener("pointercancel", clear);
    return () => {
      window.removeEventListener("pointerup", clear);
      window.removeEventListener("pointercancel", clear);
    };
  }, [pressedPage]);

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 0 14px",
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: WHEEL_W,
          maxWidth: "100%",
          perspective: 900,
          transition: "transform 0.24s cubic-bezier(0.34, 1.15, 0.64, 1), box-shadow 0.24s ease",
          transform: isLifted ? "translateY(-6px) scale(1.06)" : "translateY(0) scale(1)",
          boxShadow: isLifted
            ? "0 16px 36px rgba(102, 126, 234, 0.42), 0 8px 16px rgba(0, 0, 0, 0.22), 0 2px 6px rgba(102, 126, 234, 0.2)"
            : "none",
          borderRadius: 12,
          transformStyle: "preserve-3d",
        }}
      >
      <div
        ref={wheelRef}
        data-wheel-paginator="true"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{
          "--tx": "0px",
          display: "block",
          width: WHEEL_W,
          height: 40,
          position: "relative",
          borderRadius: 10,
          background: isD
            ? `linear-gradient(180deg, ${accent}30, ${accent}10)`
            : `linear-gradient(180deg, ${accent}22, ${accent}08)`,
          boxShadow: isD
            ? `inset 0 2px 4px rgba(255,255,255,0.08), inset 0 -2px 4px rgba(0,0,0,0.4)`
            : `inset 0 2px 4px rgba(0,0,0,0.06), inset 0 -2px 4px rgba(0,0,0,0.1)`,
          overflow: "hidden",
          cursor: "grab",
          touchAction: "none",
        }}
      >
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 8,
          background: notchBg, zIndex: 3,
          pointerEvents: "none",
          maskImage: "linear-gradient(to bottom, black 50%, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, black 50%, transparent)",
        }} />
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 8,
          background: notchBg, zIndex: 3,
          pointerEvents: "none",
          maskImage: "linear-gradient(to top, black 50%, transparent)",
          WebkitMaskImage: "linear-gradient(to top, black 50%, transparent)",
        }} />

        <div
          ref={stripParallaxRef}
          style={{
            position: "absolute", inset: 0,
            background: `repeating-linear-gradient(to right,
              ${isD ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"} 0px,
              ${isD ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"} 1px,
              transparent 1px, transparent 5px)`,
            transform: "translateX(0px)",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />

        <div
          ref={innerRef}
          style={{
            display: "flex", height: "100%",
            position: "absolute", top: 0, left: stripLeft,
            zIndex: 2,
            willChange: "transform",
          }}
        >
          {allItems.map((pg) => {
            const valid = pg >= 1 && pg <= totalPages;
            const visualPg = valid ? (totalPages - pg + 1) : pg;
            const isCurrent = pg === liveCenter;
            const isHot = valid && highlightPage === pg;
            let fontSize = WHEEL_FS.base;
            let fontWeight = 400;
            if (isHot && isCurrent) {
              fontSize = WHEEL_FS.selectedHot;
              fontWeight = 700;
            } else if (isHot || isCurrent) {
              fontSize = WHEEL_FS.selected;
              fontWeight = 700;
            }
            return (
              <div
                key={pg}
                data-wheel-page-num={valid ? "true" : undefined}
                onClick={() => valid && onPageClick(pg)}
                onMouseEnter={() => valid && setHoveredPage(pg)}
                onMouseLeave={() => valid && setHoveredPage((h) => (h === pg ? null : h))}
                onPointerDown={(e) => {
                  if (!valid) return;
                  if (e.pointerType === "touch" || e.pointerType === "pen") setPressedPage(pg);
                }}
                style={{
                  width: ITEM_W,
                  height: "100%",
                  boxSizing: "border-box",
                  padding: "2px 3px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  cursor: valid ? "pointer" : "default",
                  transition: "background 0.16s ease",
                  borderRadius: 6,
                  background: isHot ? WHEEL_HOVER_BG : "transparent",
                  outline: "none",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {valid && (
                  <span style={{
                    fontSize,
                    fontWeight,
                    color: isHot ? "#ffffff" : (isCurrent ? accent : fg),
                    opacity: isHot || isCurrent ? 1 : 0.3,
                    textShadow: isHot
                      ? "0 1px 2px rgba(0,0,0,0.25)"
                      : (isCurrent ? `0 0 10px ${accent}66` : "none"),
                    pointerEvents: "none",
                    transition: "color 0.16s ease, font-size 0.16s ease, font-weight 0.16s ease",
                  }}>{visualPg}</span>
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          position: "absolute",
          top: 2, bottom: 2,
          left: "50%",
          transform: "translateX(-50%)",
          width: ITEM_W,
          boxSizing: "border-box",
          borderRadius: 7,
          border: `1.5px solid ${accent}55`,
          background: `${accent}12`,
          pointerEvents: "none",
          zIndex: 5,
        }} />

        <div style={{
          position: "absolute", top: 0, bottom: 0, left: 0, width: 32,
          background: isD
            ? `linear-gradient(to right, ${accent}30, transparent)`
            : `linear-gradient(to right, ${accent}18, transparent)`,
          pointerEvents: "none", zIndex: 4,
        }} />
        <div style={{
          position: "absolute", top: 0, bottom: 0, right: 0, width: 32,
          background: isD
            ? `linear-gradient(to left, ${accent}30, transparent)`
            : `linear-gradient(to left, ${accent}18, transparent)`,
          pointerEvents: "none", zIndex: 4,
        }} />
      </div>
      </div>
    </div>
  );
}

export default function ProfilePage({
  token,
  lang,
  theme,
  translations,
  onLogout,
  adminRoute,
  isSamsungBrowser = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // ── Reactive isMobile ──────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Capture PWA install prompt and show it automatically
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e); // просто сохраняем
    };
  
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const isDark = !isSamsungBrowser && theme === "dark";

  // ── Pinch-to-scale-text: custom zoom via CSS `zoom` on page root ─────────
  //    Повторяет логику ChatPage: нативный пинч и двойной тап блокируются,
  //    вместо этого меняется масштаб страницы профиля. Хранится в localStorage.
  const TEXT_SCALE_MIN = 0.85;
  const TEXT_SCALE_MAX = 1.6;
  const clampTextScale = (v) =>
    Math.max(TEXT_SCALE_MIN, Math.min(TEXT_SCALE_MAX, v));
  const [textScale, setTextScale] = useState(() => {
    try {
      const saved = parseFloat(localStorage.getItem("profileTextScale"));
      return Number.isFinite(saved) && saved > 0 ? clampTextScale(saved) : 1;
    } catch { return 1; }
  });
  const textScaleRef = useRef(textScale);
  useEffect(() => {
    textScaleRef.current = textScale;
    try { localStorage.setItem("profileTextScale", String(textScale)); } catch {}
  }, [textScale]);

  useEffect(() => {
    if (!isMobile || typeof window === "undefined") return;

    // 1) Отключаем нативный pinch-zoom через meta viewport
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const prevViewport = viewportMeta?.getAttribute("content") || null;
    if (viewportMeta) {
      viewportMeta.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover"
      );
    }

    // 2) Пинч-жест → наш textScale; двойной тап — отключён
    let isPinching = false;
    let initialDistance = 0;
    let initialScale = textScaleRef.current;

    const getDistance = (t) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        isPinching = true;
        initialDistance = getDistance(e.touches);
        initialScale = textScaleRef.current;
      }
    };
    const onTouchMove = (e) => {
      if (isPinching && e.touches.length === 2) {
        e.preventDefault();
        const d = getDistance(e.touches);
        if (initialDistance > 0) {
          const next = clampTextScale(initialScale * (d / initialDistance));
          setTextScale(Math.round(next * 100) / 100);
        }
      }
    };
    const onTouchEnd = (e) => {
      if (!e.touches || e.touches.length < 2) {
        isPinching = false;
        initialDistance = 0;
      }
    };

    // 3) iOS Safari: gesture-события — глушим
    const blockGesture = (e) => { e.preventDefault(); };

    document.addEventListener("touchstart", onTouchStart, { passive: false });
    document.addEventListener("touchmove",  onTouchMove,  { passive: false });
    document.addEventListener("touchend",   onTouchEnd,   { passive: true  });
    document.addEventListener("touchcancel",onTouchEnd,   { passive: true  });
    document.addEventListener("gesturestart",  blockGesture);
    document.addEventListener("gesturechange", blockGesture);
    document.addEventListener("gestureend",    blockGesture);

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove",  onTouchMove);
      document.removeEventListener("touchend",   onTouchEnd);
      document.removeEventListener("touchcancel",onTouchEnd);
      document.removeEventListener("gesturestart",  blockGesture);
      document.removeEventListener("gesturechange", blockGesture);
      document.removeEventListener("gestureend",    blockGesture);
      if (viewportMeta && prevViewport !== null) {
        viewportMeta.setAttribute("content", prevViewport);
      }
    };
  }, [isMobile]);
  
  // ── Active sidebar section ─────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState("home");
  const matchesSectionRef = useRef(null);
  const invitedSectionRef = useRef(null);

  // Локальное состояние для переключателей событий
  const [localToggleState, setLocalToggleState] = useState({});
  
  // State variables
  const [invitedUsers, setInvitedUsers] = useState(() => readCache(CACHE_KEYS.INVITED_USERS, []));
  // If we already have cached data, skip the initial loading state so blocks render immediately
  const [loading, setLoading] = useState(() => readCache(CACHE_KEYS.INVITED_USERS, null) === null);
  const [avatarPreview, setAvatarPreview] = useState(() => {
    const saved = localStorage.getItem('userAvatar');
    return saved ? JSON.parse(saved) : null;
  });
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  const [avatarSuccess, setAvatarSuccess] = useState(false);
  const [, setSelectedButton] = useState(null);
  const [matchesByCategory, setMatchesByCategory] = useState(() =>
    readCache(CACHE_KEYS.MATCHES, {
      business_clients: [],
      startup: [],
      job: [],
      other: [],
      friends: [],
    })
  );
  const [searchRequestsByCategory, setSearchRequestsByCategory] = useState(() => readCache(CACHE_KEYS.SEARCH_REQUESTS, {}));
  const [, setSearchRequestsLoading] = useState(false);
  const [deletingRequestIds, setDeletingRequestIds] = useState({});
  const [archivingRequestIds, setArchivingRequestIds] = useState({});
  const [openMenuRequestId, setOpenMenuRequestId] = useState(null);
  // const [editingRequestIds, setEditingRequestIds] = useState({});
  const [intuitiveDiscoveryLoading, setIntuitiveDiscoveryLoading] = useState(false);
  const [hiddenMatchIds] = useState(() => {
    try {
      const raw = localStorage.getItem("hiddenProfileMatchIds");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [, setBlockedCategories] = useState([]);
  const [, setCategories] = useState(() => readCache(CACHE_KEYS.CATEGORIES, {}));
  const [, setMatchesLoading] = useState(false);
  const [profileInfo, setProfileInfo] = useState(() =>
    readCache(CACHE_KEYS.PROFILE_INFO, {
      id: null,
      request: null,
      about: null,
      profile_completed: false,
      name: null,
      role: null,
      active_event_id: null,
      active_event_ids: [],
      email: null,
      email_verified: false,
    })
  );
  const [emailCode, setEmailCode] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailConfirming, setEmailConfirming] = useState(false);
  const [emailVerifyExpanded, setEmailVerifyExpanded] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [matchingStatus, setMatchingStatus] = useState(() =>
    readCache(CACHE_KEYS.MATCHING_STATUS, {
      hasAttempt: false,
      status: null,
      isSearching: false,
    })
  );
  const [eventInviteCode, setEventInviteCode] = useState("");
  const [eventRegistrations, setEventRegistrations] = useState(() => readCache(CACHE_KEYS.EVENT_REGS, []));
  const [, setEventsLoading] = useState(false);
  const [activeEventUpdating, setActiveEventUpdating] = useState(false);
  const [eventError, setEventError] = useState("");
  const [, setLastSearchCategory] = useState(null);
  const [, setNewMatchIndicators] = useState({
    clients: false,
    startup: false,
    job: false,
    other: false,
    friends: false,
  });
  const [, setCategoryParams] = useState(() => readCache(CACHE_KEYS.CATEGORY_PARAMS, {}));
  const [timeFilter, setTimeFilter] = useState(readInvitesTimeFilter);
  const MATCHES_PER_PAGE = 2;
  /** Request / "Match by demand" history: one match card per wheel page */
  const MATCHES_PER_HISTORY_PAGE = 1;
  const [matchPage, setMatchPage] = useState(1);
  const [discoveryPage, setDiscoveryPage] = useState(1);
  const [isListVisible, setIsListVisible] = useState(true);
  const [rejectModal, setRejectModal] = useState({
    isOpen: false,
    matchId: null,
    reason: "",
    searchRequestId: null,
    matchMode: null,
  });
  const prevMatchesRef = useRef(matchesByCategory);
  const seenMatchIdsRef = useRef(new Set());
  const autoRematchTimerRef = useRef(null);
  const activePaginationRef = useRef({ page: 1, totalPages: 1, setPage: () => {} });
  const matchPageDirectionRef = useRef("forward");
  const matchesBlockRef = useRef(null);
  /** Discovery tab: scrollable match list viewport. Wheel pagination is only on `MatchesWheelPagination`. */
  const matchesScrollRef = useRef(null);
  const blockSwipeRef = useRef({ active: false, touchId: null, startX: 0, startY: 0, startPage: 1, locked: null });
  const prevDiscoveryCarouselRef = useRef(1);
  const prevMatchCarouselRef = useRef(1);

  // Wallet / Billing state
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletInitialTab, setWalletInitialTab] = useState("overview");
  const [balance, setBalance] = useState(() => readCache(CACHE_KEYS.BALANCE, null));
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(() => readCache("userQRCode", null));
  const [qrCodeDataURL, setQrCodeDataURL] = useState("");
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [qrCodeError, setQrCodeError] = useState(null);
  const [inviteQrModalOpen, setInviteQrModalOpen] = useState(false);

  // PWA install prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallHint, setShowInstallHint] = useState(false);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isFirefox = /firefox/i.test(navigator.userAgent);
  const isOpera = /opr\//i.test(navigator.userAgent);
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;

  useEffect(() => {
    if (!inviteQrModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setInviteQrModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [inviteQrModalOpen]);

  useEffect(() => {
    if (!isMobile && inviteQrModalOpen) setInviteQrModalOpen(false);
  }, [inviteQrModalOpen, isMobile]);

  // Detect browser name for hint text
  const getBrowserName = () => {
    if (isIOS && isSafari) return "safari-ios";
    if (isIOS) return "ios-other";
    if (isFirefox) return "firefox";
    if (isOpera) return "opera";
    if (/edg\//i.test(navigator.userAgent)) return "edge";
    if (/samsung/i.test(navigator.userAgent)) return "samsung";
    if (deferredPrompt) return "chrome"; // Chrome/Edge/Samsung with prompt
    return "generic";
  };

  // Matches tabs: request-based vs intuitive discovery.
  const [activeMatchesTab, setActiveMatchesTab] = useState("request");

  const findScrollContainer = useCallback((target) => {
    let node = target?.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
        node.scrollHeight > node.clientHeight
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }, []);

  const scrollToProfileSection = useCallback((targetRef, behavior = "auto") => {
    const target = targetRef.current;
    if (!target) return false;

    const appScrollRoot = target.closest("[data-app-scroll-root]");
    const scrollRoot = appScrollRoot?.scrollHeight > appScrollRoot?.clientHeight
      ? appScrollRoot
      : findScrollContainer(target);
    const offset = isMobile ? 120 : 260;

    if (scrollRoot && scrollRoot !== document.scrollingElement && scrollRoot !== document.documentElement) {
      const rootRect = scrollRoot.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const nextTop = scrollRoot.scrollTop + targetRect.top - rootRect.top - offset;
      scrollRoot.scrollTo({ top: Math.max(0, nextTop), left: 0, behavior });
      return true;
    }

    const currentTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const nextTop = currentTop + target.getBoundingClientRect().top - offset;
    window.scrollTo({ top: Math.max(0, nextTop), left: 0, behavior });
    return true;
  }, [findScrollContainer, isMobile]);

  useEffect(() => {
    try {
      localStorage.setItem(INVITES_TIME_FILTER_KEY, timeFilter);
    } catch (err) {
      console.warn("Failed to persist invites time filter:", err);
    }
  }, [timeFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get("section") || location.state?.profileSection;
    const section = requested === "invitations" ? "invites" : requested;
    let targetRef = null;

    if (section === "matches") {
      setActiveMatchesTab("request");
      setMatchPage(1);
      setActiveSection("matches");
      targetRef = matchesSectionRef;
    } else if (section === "invites") {
      setActiveSection("invites");
      targetRef = invitedSectionRef;
    }

    if (!targetRef) return undefined;
    if (loading) return undefined;

    let frameId = null;
    let timerId = null;
    let attempts = 0;

    const tryScroll = () => {
      attempts += 1;
      if (scrollToProfileSection(targetRef, "auto") || attempts >= 20) return;
      timerId = window.setTimeout(tryScroll, 80);
    };

    frameId = window.requestAnimationFrame(() => {
      timerId = window.setTimeout(tryScroll, 80);
    });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [loading, location.search, location.key, location.state, scrollToProfileSection]);

  useEffect(() => {
    try {
      localStorage.setItem("hiddenProfileMatchIds", JSON.stringify(hiddenMatchIds));
    } catch (err) {
      console.warn("Failed to persist hidden match ids:", err);
    }
  }, [hiddenMatchIds]);

  useEffect(() => {
    if (openMenuRequestId === null) return;
    const close = () => setOpenMenuRequestId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuRequestId]);

  const heroAvatarSrc = useMemo(
    () => avatarPreview || defaultAvatar,
    [avatarPreview]
  );

  // ── Matches: horizontal swipe on block (wheel pagination only on `MatchesWheelPagination`) ──
  // useLayoutEffect: block ref set before this runs (useEffect can see null on first paint)
  useLayoutEffect(() => {
    const block = matchesBlockRef.current;
    if (!block) return;

    /** Need enough movement before choosing axis; diagonals default to vertical so page scroll works */
    const AXIS_LOCK_MIN_PX = 26;
    const AXIS_DOMINANCE_RATIO = 1.45;
    /** Deliberate horizontal flick — avoids accidental flips while scrolling */
    const SWIPE_PAGE_THRESHOLD_PX = 96;

    const onTouchStart = (e) => {
      // Don't hijack touches that start on the wheel paginator — it handles its own physics
      if (e.target.closest('[data-wheel-paginator]')) return;
      if (e.target.closest(MATCH_BLOCK_TOUCH_SCROLL_ROOT)) return;
      const t = e.touches[0];
      blockSwipeRef.current = {
        active: true,
        touchId: t.identifier,
        startX: t.clientX,
        startY: t.clientY,
        startPage: activePaginationRef.current.page,
        locked: null,
      };
    };

    const onTouchMove = (e) => {
      const sw = blockSwipeRef.current;
      if (!sw.active) return;
      // Also bail if we somehow ended up on the wheel
      if (e.target.closest('[data-wheel-paginator]')) {
        blockSwipeRef.current.active = false;
        return;
      }
      if (e.target.closest(MATCH_BLOCK_TOUCH_SCROLL_ROOT)) {
        blockSwipeRef.current.active = false;
        blockSwipeRef.current.locked = null;
        return;
      }
      const t = Array.from(e.touches).find((c) => c.identifier === sw.touchId);
      if (!t) return;
      const dx = t.clientX - sw.startX;
      const dy = t.clientY - sw.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      if (sw.locked === null && (adx > AXIS_LOCK_MIN_PX || ady > AXIS_LOCK_MIN_PX)) {
        if (adx > ady * AXIS_DOMINANCE_RATIO) sw.locked = "h";
        else sw.locked = "v"; // vertical or diagonal → let the page scroll
      }

      if (sw.locked === "h") {
        e.preventDefault();
        // One page per gesture — commit on touchend only (see onTouchEnd)
      }
    };

    const onTouchEnd = (e) => {
      const sw = blockSwipeRef.current;
      const t = Array.from(e.changedTouches).find((c) => c.identifier === sw.touchId);
      if (sw.active && t && sw.locked === "h") {
        const dx = t.clientX - sw.startX;
        const dy = t.clientY - sw.startY;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        // Must stay clearly horizontal at release (avoids counting vertical scroll as swipe)
        if (adx < ady * AXIS_DOMINANCE_RATIO) {
          blockSwipeRef.current.active = false;
          blockSwipeRef.current.locked = null;
          return;
        }
        const pag = activePaginationRef.current;
        if (pag.totalPages > 1) {
          if (dx < -SWIPE_PAGE_THRESHOLD_PX && sw.startPage < pag.totalPages) {
            pag.setPage(sw.startPage + 1);
          } else if (dx > SWIPE_PAGE_THRESHOLD_PX && sw.startPage > 1) {
            pag.setPage(sw.startPage - 1);
          }
        }
      }
      blockSwipeRef.current.active = false;
      blockSwipeRef.current.locked = null;
    };

    const onTouchCancel = () => {
      blockSwipeRef.current.active = false;
      blockSwipeRef.current.locked = null;
    };

    block.addEventListener("touchstart", onTouchStart, { passive: true });
    block.addEventListener("touchmove", onTouchMove, { passive: false });
    block.addEventListener("touchend", onTouchEnd, { passive: true });
    block.addEventListener("touchcancel", onTouchCancel, { passive: true });

    return () => {
      block.removeEventListener("touchstart", onTouchStart);
      block.removeEventListener("touchmove", onTouchMove);
      block.removeEventListener("touchend", onTouchEnd);
      block.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [
    activeSection,
    activeMatchesTab,
    matchesByCategory,
    searchRequestsByCategory,
    hiddenMatchIds,
  ]);

  // Инициализация локального состояния переключателей
  useEffect(() => {
    setLocalToggleState(prev => {
      const updated = { ...prev };
      eventRegistrations.forEach(event => {
        const key = event.id || event.event_id;
        if (key != null) {
          if (isEventMatchingExpired(event)) {
            updated[key] = false;
            return;
          }
          const activeIds = Array.isArray(profileInfo.active_event_ids)
            ? profileInfo.active_event_ids
            : (profileInfo.active_event_id ? [profileInfo.active_event_id] : []);
          const isActiveByProfile = activeIds.some((id) => String(id) === String(key));
          const hasProfileScope = activeIds.length > 0 || profileInfo.active_event_id != null;
          const hasExplicitFlag = typeof event.is_active === "boolean";
          updated[key] = hasProfileScope ? isActiveByProfile : (hasExplicitFlag ? event.is_active : false);
        }
      });
      return updated;
    });
  }, [eventRegistrations, profileInfo.active_event_id, profileInfo.active_event_ids]);

  // Theme variables
  // ── Samsung Browser complete theme ────────────────────────────────────────
  // Samsung darkens everything ~30% so we go LIGHT. Bg matches particle (#eceeff).
  // Buttons 2 tones brighter than default to survive Samsung crushing.
  const ST = {
    pageBg:      "#f4f7ff",
    panel:       "#eef3ff",
    navPill:     "#e7ecff",
    tooltip:     "#e5ebfb",
    hero:        "#edf2ff",
    card:        "rgba(255,255,255,0.78)",
    cardNested:  "#eef3ff",
    input:       "#e7ecff",
    soonBadge:   "rgba(67,56,202,0.15)",
    balanceBtn:  "linear-gradient(135deg, #ddd8fc 0%, #e9d8fe 100%)",
    installBtn:  "linear-gradient(135deg, #ddd8fc 0%, #e9d8fe 100%)",
    logoutBg:    "rgba(220,50,50,0.13)",
    logoutHover: "rgba(220,50,50,0.24)",
    fg:          "#172033",
    fgMuted:     "#56637f",
    border:      "#8b95ea",
    btnBg:       "#7b83e6",
    btnFg:       "#1e1b4b",
    disabledBg:  "#c7d2fe",
    gradient:    "#a5b4fc",
    success:     "#4ade80",
    warning:     "#fcd34d",
    error:       "#fca5a5",
    organizerBg: "linear-gradient(135deg, #fde68a 0%, #fb923c 60%, #f97316 100%)",
  };

  const themeVars = isSamsungBrowser
    ? {
        bg:         ST.pageBg,
        fg:         ST.fg,
        card:       ST.card,
        border:     ST.border,
        btnBg:      ST.btnBg,
        btnFg:      ST.btnFg,
        success:    ST.success,
        warning:    ST.warning,
        error:      ST.error,
        disabledBg: ST.disabledBg,
        gradient:   ST.gradient,
      }
    : theme === "dark"
    ? {
        bg: "#1a1a2e",
        fg: "#ffffff",
        card: "#16213e",
        border: "#667eea",
        btnBg: "transparent",
        btnFg: "#ffffff",
        success: "#0abf30",
        warning: "#e9bd0c",
        error: "#f24d4c",
        disabledBg: "#667eea",
        gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      }
    : LH_PROFILE.themeVars;

  const reverseCategoryKeyMap = useMemo(() => ({
    business_clients: "clients",
    startup: "startup",
    job: "job",
    other: "other",
    friends: "friends",
  }), []);

  // Load invited users
  const loadInvitedUsers = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}${QR_ENDPOINTS.MY_INVITED_USERS}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const sortedUsers = (data.invited_users || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setInvitedUsers(sortedUsers);
      writeCache(CACHE_KEYS.INVITED_USERS, sortedUsers);
    } catch (e) {
      console.error(e);
      setInvitedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Load wallet balance
  const loadBalance = useCallback(async ({ silent = false } = {}) => {
    if (!token) return;
    try {
      if (!silent) setBalanceLoading(true);
      const { data } = await axios.get(`${API_URL}/api/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bal = data.balance ?? data.amount ?? 0;
      setBalance(bal);
      writeCache(CACHE_KEYS.BALANCE, bal);
    } catch (err) {
      console.error("Error loading balance:", err);
      setBalance(0);
    } finally {
      if (!silent) setBalanceLoading(false);
    }
  }, [token]);

  const handleWalletBalanceUpdated = useCallback((nextBalance) => {
    setBalance(nextBalance);
    writeCache(CACHE_KEYS.BALANCE, nextBalance);
  }, []);

  const topupReturnHandledRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const topup = params.get("topup");
    if (!topup || topupReturnHandledRef.current) return;
    topupReturnHandledRef.current = true;

    params.delete("topup");
    params.delete("session_id");
    const cleaned =
      location.pathname +
      (params.toString() ? `?${params.toString()}` : "") +
      location.hash;
    window.history.replaceState({}, "", cleaned);

    if (topup === "success") {
      toast(
        lang === "fr"
          ? "Paiement réussi ! Votre solde sera mis à jour sous peu."
          : lang === "ru"
          ? "Оплата прошла! Баланс обновится через несколько секунд."
          : "Payment successful! Your balance will update shortly.",
        "success"
      );
      loadBalance();
      let attempts = 0;
      const tick = () => {
        attempts += 1;
        loadBalance({ silent: true });
        if (attempts < 6) {
          setTimeout(tick, 2000);
        }
      };
      setTimeout(tick, 2000);
    } else if (topup === "cancel") {
      toast(
        lang === "fr" ? "Paiement annulé" : lang === "ru" ? "Оплата отменена" : "Payment cancelled",
        "info"
      );
    }
  }, [location.search, location.pathname, location.hash, lang, toast, loadBalance]);

  // Open wallet modal (overview) or jump straight to Stripe top-up tab
  const openWallet = (tab = "overview") => {
    setWalletInitialTab(tab === "topup" ? "topup" : "overview");
    setWalletOpen(true);
  };

  const generateInviteUrl = useCallback((personalQrCode, qrData = null) => {
    if (!personalQrCode) return null;
    const fallbackUrl = `${window.location.origin}/register?invite_code=${personalQrCode}`;
    if (!qrData?.registration_url) return fallbackUrl;
    try {
      const parsed = new URL(qrData.registration_url);
      return parsed.hostname === window.location.hostname ? qrData.registration_url : fallbackUrl;
    } catch {
      return fallbackUrl;
    }
  }, []);

  const generateQRCodeImage = useCallback(async (personalQrCode, qrData = null) => {
    const inviteUrl = generateInviteUrl(personalQrCode, qrData);
    if (!inviteUrl) return;

    const qr = QRCode.create(inviteUrl, { errorCorrectionLevel: "H" });
    const modules = qr.modules;
    const size = modules.size;

    const cell = 16;
    const quiet = 2;
    const dim = (size + quiet * 2) * cell;
    const color = "#5B5BF5";
    const bg = "#ffffff";

    const canvas = document.createElement("canvas");
    canvas.width = dim;
    canvas.height = dim;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, dim, dim);

    const roundedRect = (x, y, w, h, r) => {
      const radius = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    };

    const inFinder = (x, y) =>
      (x < 7 && y < 7) ||
      (x >= size - 7 && y < 7) ||
      (x < 7 && y >= size - 7);

    ctx.fillStyle = color;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (!modules.get(x, y)) continue;
        if (inFinder(x, y)) continue;
        const cx = (x + quiet) * cell + cell / 2;
        const cy = (y + quiet) * cell + cell / 2;
        ctx.beginPath();
        ctx.arc(cx, cy, cell * 0.42, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const drawFinder = (gx, gy) => {
      const px = (gx + quiet) * cell;
      const py = (gy + quiet) * cell;
      ctx.fillStyle = color;
      roundedRect(px, py, 7 * cell, 7 * cell, cell * 1.8);
      ctx.fill();
      ctx.fillStyle = bg;
      roundedRect(px + cell, py + cell, 5 * cell, 5 * cell, cell * 1.3);
      ctx.fill();
      ctx.fillStyle = color;
      roundedRect(px + 2 * cell, py + 2 * cell, 3 * cell, 3 * cell, cell * 0.9);
      ctx.fill();
    };

    drawFinder(0, 0);
    drawFinder(size - 7, 0);
    drawFinder(0, size - 7);

    setQrCodeDataURL(canvas.toDataURL("image/png"));
  }, [generateInviteUrl]);

  const loadPermanentQRCode = useCallback(async () => {
    if (!token) return;
    let hasUsableQr = false;
    try {
      setQrCodeLoading(true);
      setQrCodeError(null);

      const cachedQRCode = readCache("userQRCode", null);
      if (cachedQRCode?.personal_qr_code) {
        hasUsableQr = true;
        setQrCodeData(cachedQRCode);
        await generateQRCodeImage(cachedQRCode.personal_qr_code, cachedQRCode);
      }

      const qrEndpoint = QR_ENDPOINTS.MY_QR?.startsWith("http")
        ? QR_ENDPOINTS.MY_QR
        : `${API_URL}${QR_ENDPOINTS.MY_QR}`;
      const { data } = await axios.get(qrEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setQrCodeData(data);
      writeCache("userQRCode", data);
      hasUsableQr = !!data?.personal_qr_code;
      await generateQRCodeImage(data.personal_qr_code, data);
    } catch (err) {
      console.error("Error loading QR code:", err);
      if (!hasUsableQr) {
        setQrCodeError(translations[lang]?.qrCodeError || "Failed to load QR code");
      }
    } finally {
      setQrCodeLoading(false);
    }
  }, [generateQRCodeImage, lang, token, translations]);

  // Load search requests by category
  const loadSearchRequestsByCategory = useCallback(async () => {
    if (!token) {
      console.warn("Нет токена — пропускаем загрузку search requests");
      return;
    }
    try {
      setSearchRequestsLoading(true);
      const { data } = await axios.get(
        `${API_URL}/api/search_requests_user_list/by_category`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        if (data.search_requests_by_category) {
          setSearchRequestsByCategory(data.search_requests_by_category || {});
          writeCache(CACHE_KEYS.SEARCH_REQUESTS, data.search_requests_by_category || {});
        } else if (Array.isArray(data.search_requests)) {
          setSearchRequestsByCategory({ all: data.search_requests });
          writeCache(CACHE_KEYS.SEARCH_REQUESTS, { all: data.search_requests });
        } else {
          setSearchRequestsByCategory({});
        }
      }
    } catch (err) {
      console.error("Error loading search requests:", err);
      if (err.response?.status === 401) {
        // handle logout
      } else {
        setSearchRequestsByCategory({});
      }
    } finally {
      setSearchRequestsLoading(false);
    }
  }, [token, navigate, toast]);

  // Load profile info
  const loadProfileInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/profile_status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      if (data) {
        const nextInfo = {
          id: data.id || null,
          request: data.request || null,
          about: data.about || null,
          profile_completed: data.profile_completed || false,
          name: data.name || null,
          role: data.role || null,
          active_event_id: data.active_event_id || null,
          active_event_ids: Array.isArray(data.active_event_ids)
            ? data.active_event_ids
            : (data.active_event_id ? [data.active_event_id] : []),
          email: data.email || null,
          email_verified: !!data.email_verified,
        };
        setProfileInfo(nextInfo);
        writeCache(CACHE_KEYS.PROFILE_INFO, nextInfo);

        if (data.id) {
          try {
            const avatarRes = await axios.get(
              `${API_URL}/api/avatar/${data.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 3000
              }
            );
            if (avatarRes.data.has_custom_avatar && avatarRes.data.direct_url) {
              const avatarUrl = avatarRes.data.direct_url;
              setAvatarPreview(avatarUrl);
              localStorage.setItem('userAvatar', JSON.stringify(avatarUrl));
              localStorage.setItem('userAvatarId', data.id.toString());
              localStorage.setItem('userAvatarTimestamp', Date.now().toString());
              console.log("✅ Loaded avatar from API and saved to localStorage: ", avatarUrl);
            } else {
              setAvatarPreview(defaultAvatar);
              localStorage.removeItem('userAvatar');
            }
          } catch (avatarErr) {
            console.warn("⚠️ Could not load avatar: ", avatarErr.message);
            const savedAvatar = localStorage.getItem('userAvatar');
            if (savedAvatar) {
              setAvatarPreview(JSON.parse(savedAvatar));
              console.log("✅ Using cached avatar from localStorage");
            } else {
              setAvatarPreview(defaultAvatar);
            }
          }
        } else {
          setAvatarPreview(defaultAvatar);
          localStorage.removeItem('userAvatar');
        }
      }
    } catch (err) {
      console.error("Error loading profile info: ", err);
      const savedAvatar = localStorage.getItem('userAvatar');
      if (savedAvatar) {
        setAvatarPreview(JSON.parse(savedAvatar));
        console.log("✅ Using cached avatar from localStorage after error");
      } else {
        setAvatarPreview(defaultAvatar);
      }
    }
  }, [token]);

  const sendEmailVerificationCode = async () => {
    try {
      setEmailSending(true);
      const { data } = await axios.post(
        `${API_URL}/api/email/verification/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast(data?.message || (lang === "fr" ? "Code envoyé par email" : "Verification code sent"), "success");
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || (lang === "fr" ? "Erreur d'envoi du code" : "Failed to send code");
      toast(msg, "error");
      return false;
    } finally {
      setEmailSending(false);
    }
  };

  const confirmEmailVerificationCode = async () => {
    if (!emailCode.trim()) {
      toast(lang === "fr" ? "Saisissez le code" : "Enter the verification code", "error");
      return;
    }
    try {
      setEmailConfirming(true);
      const { data } = await axios.post(
        `${API_URL}/api/email/verification/confirm`,
        { code: emailCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfileInfo((prev) => ({ ...prev, email_verified: true }));
      setEmailCode("");
      toast(data?.message || (lang === "fr" ? "Email confirmé" : "Email verified"), "success");
    } catch (err) {
      const msg = err.response?.data?.detail || (lang === "fr" ? "Code invalide" : "Invalid verification code");
      toast(msg, "error");
    } finally {
      setEmailConfirming(false);
    }
  };

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/categories`, {
        params: { lang },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setCategories(data.categories || {});
        writeCache(CACHE_KEYS.CATEGORIES, data.categories || {});
      }
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  }, [token, lang]);

  // Load matches by categories
  const loadMatchesByCategories = useCallback(async () => {
    try {
      setMatchesLoading(true);
      const { data } = await axios.get(`${API_URL}/api/matches/by_category`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        const newData = data.matches_by_category || {};
        setMatchesByCategory(newData);
        writeCache(CACHE_KEYS.MATCHES, newData);
        prevMatchesRef.current = newData;
        console.log("Загружены матчи:", newData);
        if (data.blocked_categories) setBlockedCategories(data.blocked_categories);
      }
    } catch (err) {
      console.error("Error loading matches:", err.response?.data || err.message);
    } finally {
      setMatchesLoading(false);
    }
  }, [token]);

  // Load matching status
  const loadMatchingStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/matching_status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data) {
        const isSearching = ["pending", "in_progress"].includes(data.status);
        if (isSearching && data.category) setLastSearchCategory(data.category);
        const nextStatus = {
          hasAttempt: data.has_attempt || false,
          status: data.status,
          category: data.category,
          isSearching,
          attemptId: data.attempt_id,
          candidatesAnalyzed: data.candidates_analyzed
        };
        setMatchingStatus(nextStatus);
        writeCache(CACHE_KEYS.MATCHING_STATUS, nextStatus);
        if (!isSearching) {
          setLastSearchCategory(null);
        }
      }
    } catch (err) {
      console.error("Error loading matching status:", err);
    }
  }, [token]);

  const handleDeleteSearchRequest = async (searchRequest) => {
    const requestId = searchRequest?.id;
    if (!requestId) return;
    const preview = (searchRequest?.request_text || "").trim();
    const confirmText = lang === "fr"
      ? `Supprimer cette requête ?\n\n${preview ? `"${preview.slice(0, 120)}"` : ""}\n\nCette action est irréversible.`
      : `Delete this search request?\n\n${preview ? `"${preview.slice(0, 120)}"` : ""}\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmText)) return;

    try {
      setDeletingRequestIds((prev) => ({ ...prev, [requestId]: true }));
      await axios.delete(`${API_URL}/api/search_requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast(lang === "fr" ? "Requête supprimée" : "Search request deleted", "success");
      await Promise.all([
        loadSearchRequestsByCategory(),
        loadMatchesByCategories(),
        loadMatchingStatus()
      ]);
    } catch (err) {
      console.error("Error deleting search request:", err);
      toast(
        err.response?.data?.detail || (lang === "fr" ? "Erreur de suppression" : "Failed to delete search request"),
        "error"
      );
    } finally {
      setDeletingRequestIds((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleArchiveSearchRequest = async (searchRequest) => {
    const requestId = searchRequest?.id;
    if (!requestId) return;
    const archiving = searchRequest?.status !== "archived";
    try {
      setArchivingRequestIds((prev) => ({ ...prev, [requestId]: true }));
      await axios.put(
        `${API_URL}/api/search_requests/${requestId}`,
        archiving
          ? { status: "archived", is_active: false }
          : { status: "active", is_active: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast(
        archiving
          ? (lang === "fr" ? "Demande archivée" : "Request archived")
          : (lang === "fr" ? "Demande réactivée" : "Request reactivated"),
        "success"
      );
      await Promise.all([
        loadSearchRequestsByCategory(),
        loadMatchesByCategories(),
        loadMatchingStatus()
      ]);
    } catch (err) {
      console.error("Error archiving search request:", err);
      toast(
        err.response?.data?.detail || (lang === "fr" ? "Erreur d'archivage" : "Failed to archive request"),
        "error"
      );
    } finally {
      setArchivingRequestIds((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  // const handleEditSearchRequest = async (searchRequest) => {
  //   const requestId = searchRequest?.id;
  //   if (!requestId) return;
  //
  //   const currentText = (searchRequest?.request_text || "").trim();
  //   const promptLabel = lang === "fr"
  //     ? "Modifier la demande"
  //     : lang === "ru"
  //       ? "Изменить запрос"
  //       : "Edit search request";
  //   const nextTextRaw = window.prompt(promptLabel, currentText);
  //   if (nextTextRaw == null) return;
  //   const nextText = nextTextRaw.trim();
  //   if (!nextText) {
  //     toast(
  //       lang === "fr"
  //         ? "Le texte de la demande ne peut pas être vide"
  //         : lang === "ru"
  //           ? "Текст запроса не может быть пустым"
  //           : "Request text cannot be empty",
  //       "warning"
  //     );
  //     return;
  //   }
  //   if (nextText === currentText) return;
  //
  //   try {
  //     setEditingRequestIds((prev) => ({ ...prev, [requestId]: true }));
  //
  //     await axios.put(
  //       `${API_URL}/api/search_requests/${requestId}`,
  //       { request_text: nextText },
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //
  //     try {
  //       await axios.post(
  //         `${API_URL}/api/search_requests/${requestId}/start_matching`,
  //         {},
  //         { headers: { Authorization: `Bearer ${token}` } }
  //       );
  //     } catch (startErr) {
  //       if (startErr?.response?.status !== 409) {
  //         throw startErr;
  //       }
  //     }
  //
  //     toast(
  //       lang === "fr"
  //         ? "Demande mise à jour. Matching relancé."
  //         : lang === "ru"
  //           ? "Запрос обновлен. Матчинг перезапущен."
  //           : "Request updated. Matching restarted.",
  //       "success"
  //     );
  //
  //     await Promise.all([
  //       loadSearchRequestsByCategory(),
  //       loadMatchesByCategories(),
  //       loadMatchingStatus()
  //     ]);
  //   } catch (err) {
  //     console.error("Error editing search request:", err);
  //     toast(
  //       err.response?.data?.detail || (lang === "fr" ? "Erreur de modification" : "Failed to edit request"),
  //       "error"
  //     );
  //   } finally {
  //     setEditingRequestIds((prev) => ({ ...prev, [requestId]: false }));
  //   }
  // };

  const getLatestSearchRequestId = useCallback(() => {
    const allRequests = Object.values(searchRequestsByCategory)
      .flatMap((reqs) => (Array.isArray(reqs) ? reqs : []))
      .filter((sr) => sr && sr.id != null)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return allRequests[0]?.id ?? null;
  }, [searchRequestsByCategory]);

  const resolveAutoRematchRequestId = useCallback(async (preferredRequestId = null) => {
    if (preferredRequestId) return preferredRequestId;

    const fromState = getLatestSearchRequestId();
    if (fromState) return fromState;

    try {
      const { data } = await axios.get(
        `${API_URL}/api/search_requests_user_list/by_category`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let list = [];
      if (Array.isArray(data?.search_requests)) {
        list = data.search_requests;
      } else if (data?.search_requests_by_category) {
        list = Object.values(data.search_requests_by_category)
          .flatMap((reqs) => (Array.isArray(reqs) ? reqs : []));
      }
      const sorted = list
        .filter((sr) => sr && sr.id != null)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      return sorted[0]?.id ?? null;
    } catch (err) {
      console.error("Failed to resolve auto rematch request id:", err);
      return null;
    }
  }, [getLatestSearchRequestId, token]);

  const scheduleSingleAutoRematch = useCallback(async (matchContext = {}) => {
    if (activeMatchesTab !== "request") return;
    if (matchContext?.match_mode === "intuitive_discovery") return;

    if (autoRematchTimerRef.current) {
      clearTimeout(autoRematchTimerRef.current);
      autoRematchTimerRef.current = null;
    }

    toast(
      lang === "fr"
        ? "Relance automatique du matching dans 30 secondes."
        : lang === "ru"
          ? "Автопоиск следующего мэтча через 30 секунд."
          : "Automatic rematch will start in 30 seconds.",
      "info"
    );

    const preferredRequestId = matchContext?.search_request_id ?? null;
    autoRematchTimerRef.current = setTimeout(async () => {
      try {
        const targetRequestId = await resolveAutoRematchRequestId(preferredRequestId);
        if (!targetRequestId) {
          toast(
            lang === "fr"
              ? "Aucune demande active pour relancer le matching."
              : lang === "ru"
                ? "Нет активного запроса для автопоиска."
                : "No active request found for auto rematch.",
            "warning"
          );
          return;
        }

        await axios.post(
          `${API_URL}/api/search_requests/${targetRequestId}/start_matching`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast(
          lang === "fr"
            ? "Recherche automatique relancée."
            : lang === "ru"
              ? "Автопоиск запущен."
              : "Automatic rematch started.",
          "success"
        );
      } catch (err) {
        if (err?.response?.status === 409) {
          toast(
            lang === "fr"
              ? "Matching déjà en cours pour cette demande."
              : lang === "ru"
                ? "Поиск по этому запросу уже запущен."
                : "Matching for this request is already running.",
            "info"
          );
        } else {
          console.error("Error starting auto rematch:", err);
          toast(
            err.response?.data?.detail ||
              (lang === "fr"
                ? "Échec du démarrage automatique du matching."
                : lang === "ru"
                  ? "Не удалось автоматически запустить поиск."
                  : "Failed to start automatic rematch."),
            "error"
          );
        }
      } finally {
        autoRematchTimerRef.current = null;
        await Promise.all([
          loadSearchRequestsByCategory(),
          loadMatchesByCategories(),
          loadMatchingStatus(),
        ]);
      }
    }, 30000);
  }, [
    activeMatchesTab,
    lang,
    loadMatchingStatus,
    loadMatchesByCategories,
    loadSearchRequestsByCategory,
    resolveAutoRematchRequestId,
    toast,
    token,
  ]);

  const handleIntuitiveDiscovery = async () => {
    const aboutText = (profileInfo?.about || "").trim();
    if (!aboutText || intuitiveDiscoveryLoading) return;

    try {
      setIntuitiveDiscoveryLoading(true);
      const { data } = await axios.post(
        `${API_URL}/api/intuitive_discovery/start`,
        { lang },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data?.success && data?.match_created) {
        toast(data.message || (lang === "fr" ? "Correspondance créée" : "Match created"), "success");
      } else if (data?.success) {
        toast(data.message || (lang === "fr" ? "Aucun candidat trouvé" : "No candidates found"), "warning");
      } else {
        toast(data?.message || (lang === "fr" ? "Erreur de découverte" : "Discovery failed"), "error");
      }

      setActiveMatchesTab("discovery");
      setMatchPage(1);
      await Promise.all([
        loadMatchesByCategories(),
        loadMatchingStatus()
      ]);
    } catch (err) {
      console.error("Error running intuitive discovery:", err);
      toast(
        err.response?.data?.detail || err.response?.data?.message ||
          (lang === "fr" ? "Erreur de découverte intuitive" : "Intuitive discovery error"),
        "error"
      );
    } finally {
      setIntuitiveDiscoveryLoading(false);
    }
  };

  // Load event registrations
  const loadEventRegistrations = useCallback(async () => {
    if (!token) return;
    try {
      setEventsLoading(true);
      setEventError("");
      const { data } = await axios.get(`${API_URL}/api/events/my_registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(data) ? data : [];
      setEventRegistrations(list);
      writeCache(CACHE_KEYS.EVENT_REGS, list);
    } catch (err) {
      console.error("Error loading event registrations:", err);
      setEventRegistrations([]);
      setEventError(err.response?.data?.detail || "Failed to load events");
    } finally {
      setEventsLoading(false);
    }
  }, [token]);

  // Load category params
  const loadCategoryParams = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/category_params`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setCategoryParams(data.params || {});
        writeCache(CACHE_KEYS.CATEGORY_PARAMS, data.params || {});
      }
    } catch (err) {
      console.error("Error loading category params:", err);
    }
  }, [token]);

  // Handle avatar upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast(lang === "fr" ? "Fichier trop volumineux (max 5MB)" : "File too large (max 5MB)", "error");
      return;
    }
    if (!profileInfo.id) {
      toast(lang === "fr" ? "Erreur: ID utilisateur non trouvé" : "Error: user ID not found", "error");
      return;
    }
    const inputElement = e.target;
    try {
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
      localStorage.setItem('userAvatar', JSON.stringify(previewUrl));
      localStorage.setItem('userAvatarUploading', 'true');
      setIsAvatarLoading(true);
      setAvatarSuccess(false);

      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API_URL}/api/avatar/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        toast(
          lang === "fr" ? "Avatar mis à jour !" : "Avatar updated!",
          "success"
        );
        const newAvatarUrl = response.data.direct_url || response.data.avatar_url;
        setAvatarSuccess(true);
        setAvatarPreview(newAvatarUrl);
        localStorage.setItem('userAvatar', JSON.stringify(newAvatarUrl));
        localStorage.setItem('userAvatarId', profileInfo.id.toString());
        localStorage.setItem('userAvatarTimestamp', Date.now().toString());
        localStorage.removeItem('userAvatarUploading');
        console.log("✅ Avatar updated and saved to localStorage: ", newAvatarUrl);
        setTimeout(() => setAvatarSuccess(false), 500);
      }
    } catch (err) {
      console.error("Error uploading avatar: ", err);
      const previousAvatar = localStorage.getItem('userAvatar');
      if (previousAvatar && !localStorage.getItem('userAvatarUploading')) {
        setAvatarPreview(JSON.parse(previousAvatar));
      } else {
        setAvatarPreview(defaultAvatar);
        localStorage.removeItem('userAvatar');
      }
      localStorage.removeItem('userAvatarUploading');
      setAvatarSuccess(false);
      const errorMsg = err.code === 'ECONNABORTED'
        ? (lang === "fr" ? "Téléchargement trop long (30s max)" : "Upload timeout (30s max)")
        : (err.response?.data?.detail || (lang === "fr" ? "Erreur de téléchargement" : "Upload error"));
      toast(errorMsg, "error");
    } finally {
      setIsAvatarLoading(false);
      if (inputElement && inputElement.files?.[0]) {
        inputElement.value = null;
      }
      if (avatarPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    }
  };

  // Confirm match
  const confirmMatch = async (match, accepted) => {
    const matchId = match?.id;
    if (!matchId || typeof matchId !== 'number') {
      console.error("confirmMatch: matchId некорректный или undefined", matchId);
      toast(lang === "fr" ? "Erreur: ID de match invalide" : "Error: invalid match ID", "error");
      return;
    }
    console.log("confirmMatch: отправляем запрос для матча ", matchId, "accepted: ", accepted);
    try {
      await axios.post(
        `${API_URL}/api/matches/${matchId}/confirm`,
        { accepted: accepted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast(
        accepted ? (lang === "fr" ? "Match accepté !" : "Match accepted!") : (lang === "fr" ? "Match refusé !" : "Match declined!"),
        accepted ? "success" : "warning"
      );
      await scheduleSingleAutoRematch(match);
      await loadMatchesByCategories();
    } catch (err) {
      console.error("Error confirming match: ", err);
      toast(lang === "fr" ? "Erreur lors de la confirmation" : "Error confirming match", "error");
    }
  };

  // Open reject modal
  const openRejectModal = (match) => {
    const matchId = match?.id;
    if (!matchId || typeof matchId !== 'number') {
      console.error("openRejectModal: matchId некорректный или undefined", matchId);
      toast(lang === "fr" ? "Erreur: ID de match invalide" : "Error: invalid match ID", "error");
      return;
    }
    setRejectModal({
      isOpen: true,
      matchId,
      reason: "",
      searchRequestId: match?.search_request_id ?? null,
      matchMode: match?.match_mode ?? null,
    });
  };

  // Close reject modal
  const closeRejectModal = () => {
    setRejectModal(prev => ({ ...prev, isOpen: false }));
  };

  // Handle reject with reason
  const handleRejectWithReason = async () => {
    const { matchId, reason, searchRequestId, matchMode } = rejectModal;
    if (!matchId || typeof matchId !== 'number') {
      console.error("handleRejectWithReason: matchId некорректный или undefined", matchId);
      toast(lang === "fr" ? "Erreur: ID de match invalide" : "Error: invalid match ID", "error");
      return;
    }
    if (!reason.trim()) {
      toast(
        lang === "fr"
          ? "Veuillez indiquer une raison"
          : "Please provide a reason",
        "warning"
      );
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/matches/${matchId}/confirm`,
        {
          accepted: false,
          reason: reason.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast(
        lang === "fr"
          ? "Match refusé. Merci pour votre retour !"
          : "Match declined. Thank you for your feedback!",
        "warning"
      );
      await scheduleSingleAutoRematch({
        id: matchId,
        search_request_id: searchRequestId,
        match_mode: matchMode,
      });
      await loadMatchesByCategories();
      closeRejectModal();
    } catch (err) {
      console.error("Error declining match: ", err);
      toast(
        err.response?.data?.detail ||
          (lang === "fr" ? "Erreur lors du refus" : "Error declining match"),
        "error"
      );
    }
  };

  // Join event by invite code
  const joinEventByInvite = async () => {
    if (!eventInviteCode.trim()) {
      toast(lang === "fr" ? "Code d'invitation requis" : "Invite code required", "warning");
      return;
    }
    try {
      setEventError("");
      await axios.post(
        `${API_URL}/api/events/join_by_invite`,
        { invite_code: eventInviteCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEventInviteCode("");
      await loadEventRegistrations();
      toast(lang === "fr" ? "Événement lié" : "Event linked", "success");
    } catch (err) {
      console.error("Error joining event by invite: ", err);
      const message = err.response?.data?.detail || (lang === "fr" ? "Erreur de liaison" : "Failed to link event");
      setEventError(message);
      toast(message, "error");
    }
  };

  const updateActiveEvent = async (nextEventIds) => {
    const normalizedNextIds = Array.isArray(nextEventIds)
      ? nextEventIds
          .map((id) => String(id))
          .filter((id) => /^\d+$/.test(id))
          .filter((id, idx, arr) => arr.indexOf(id) === idx)
      : [];
    const prevActiveIds = Array.isArray(profileInfo.active_event_ids)
      ? profileInfo.active_event_ids
      : (profileInfo.active_event_id ? [profileInfo.active_event_id] : []);

    // Optimistic UI update to avoid switch flicker while request is in flight.
    setProfileInfo(prev => ({
      ...prev,
      active_event_id: normalizedNextIds[0] || null,
      active_event_ids: normalizedNextIds,
    }));
    setLocalToggleState(() => {
      const updated = {};
      eventRegistrations.forEach((eventItem) => {
        const key = eventItem.id || eventItem.event_id;
        if (key != null) {
          updated[key] = normalizedNextIds.some((id) => String(id) === String(key));
        }
      });
      return updated;
    });

    try {
      setActiveEventUpdating(true);
      setEventError("");
      const payload = {
        event_ids: normalizedNextIds,
        event_id: normalizedNextIds.length > 0 ? normalizedNextIds[0] : null,
      };
      const { data } = await axios.post(
        `${API_URL}/api/events/active`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const nextActiveEventIds = Array.isArray(data.active_event_ids)
        ? data.active_event_ids
        : (data.active_event_id ? [data.active_event_id] : []);
      setProfileInfo(prev => ({
        ...prev,
        active_event_id: nextActiveEventIds[0] || null,
        active_event_ids: nextActiveEventIds,
      }));

      setLocalToggleState(() => {
        const updated = {};
        eventRegistrations.forEach((eventItem) => {
          const key = eventItem.id || eventItem.event_id;
          if (key != null) {
            updated[key] = nextActiveEventIds.some((id) => String(id) === String(key));
          }
        });
        return updated;
      });

      await loadEventRegistrations();
      await loadProfileInfo();
    } catch (err) {
      console.error("Error updating active event:", err);
      setProfileInfo(prev => ({
        ...prev,
        active_event_id: prevActiveIds[0] || null,
        active_event_ids: prevActiveIds,
      }));
      const message = err.response?.data?.detail || (lang === "fr" ? "Erreur de mise à jour" : "Failed to update active event");
      setEventError(message);
      toast(message, "error");
      await loadEventRegistrations();
    } finally {
      setActiveEventUpdating(false);
    }
  };

  // Handle logout
  const handleLogoutClick = () => {
    localStorage.removeItem("userAvatar");
    localStorage.removeItem("userAvatarId");
    localStorage.removeItem("userAvatarTimestamp");
    localStorage.removeItem("userAvatarUploading");
    localStorage.removeItem("userQRCode");
    localStorage.removeItem("inviteCode");
    localStorage.removeItem("token");
    localStorage.setItem("_was_logged_out", "true");
    clearProfileCache(); // ← сброс stale-while-revalidate кэша
    if (onLogout) onLogout();
    window.location.href = "/";
  };

  // Go to create event
  const goToCreateEvent = () => {
    navigate("/events/create");
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString(
      lang === "fr" ? "fr-FR" : "en-US",
      { year: "numeric", month: "short", day: "numeric" }
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    const datePart = d.toLocaleDateString(
      lang === "fr" ? "fr-FR" : "en-US",
      { month: "short", day: "numeric", year: "numeric" }
    );
    const timePart = d.toLocaleTimeString(
      lang === "fr" ? "fr-FR" : "en-US",
      { hour: "2-digit", minute: "2-digit", hour12: false }
    );
    return `${datePart} · ${timePart}`;
  };

  // Map old status
  const mapOldStatus = (status) => {
    if (status === "active") return "accepted";
    if (status === "pending") return "pending";
    if (status === "rejected") return "rejected";
    return status;
  };

  // Get status text
  const getStatusText = (myStatus) => {
    const normalized = mapOldStatus(myStatus);
    const texts = {
      pending: { en: "Pending", fr: "En attente" },
      accepted: { en: "Accepted", fr: "Accepté" },
      rejected: { en: "Rejected", fr: "Refusé" }
    };
    return texts[normalized]?.[lang === "fr" ? "fr" : "en"] || normalized || "";
  };

  // Get status color
  const getStatusColor = (myStatus) => {
    const normalized = mapOldStatus(myStatus);
    const colors = {
      pending: "#ffc107",
      accepted: "#28a745",
      rejected: "#dc3545"
    };
    return colors[normalized] || "#667eea";
  };

  // Cleanup effect for avatar blob URLs
  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // Load invited users
  useEffect(() => {
    loadInvitedUsers();
  }, [token]);

  // Load data on mount — all requests fire in parallel so fresh data arrives ASAP.
  // Cached data (loaded into state via useState initialisers above) is shown immediately.
  useEffect(() => {
    Promise.all([
      loadCategories(),
      loadMatchesByCategories(),
      loadProfileInfo(),
      loadMatchingStatus(),
      loadCategoryParams(),
      loadEventRegistrations(),
      loadBalance(),
      loadPermanentQRCode(),
    ]).catch(() => {/* individual loaders handle their own errors */});
  }, [
    loadCategories,
    loadMatchesByCategories,
    loadProfileInfo,
    loadMatchingStatus,
    loadCategoryParams,
    loadEventRegistrations,
    loadBalance,
    loadPermanentQRCode
  ]);

  // Auto-refresh matches every 5s so new matches appear without a reload
  useEffect(() => {
    const interval = setInterval(() => {
      loadMatchesByCategories();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadMatchesByCategories]);

  // When matching finishes (isSearching flips to false), immediately fetch new matches
  const prevIsSearchingRef = useRef(matchingStatus.isSearching);
  useEffect(() => {
    if (prevIsSearchingRef.current === true && matchingStatus.isSearching === false) {
      loadMatchesByCategories();
    }
    prevIsSearchingRef.current = matchingStatus.isSearching;
  }, [matchingStatus.isSearching, loadMatchesByCategories]);

  // Check for new matches
  useEffect(() => {
    const prev = prevMatchesRef.current;
    const current = matchesByCategory;
    const currentPending = new Set();
    Object.values(current).flat().forEach(m => {
      if (m.my_status === "pending") currentPending.add(m.id);
    });
    const prevPending = new Set();
    Object.values(prev).flat().forEach(m => {
      if (m.my_status === "pending") prevPending.add(m.id);
    });
    const newPending = [...currentPending].filter(id => !prevPending.has(id));
    if (newPending.length > 0) {
      let message = "✨ New match awaiting your approval!";
      if (lang === "fr") {
        message = "✨ Nouveau match en attente de votre approbation!";
      }
      toast(message, "success", 10000);
    }
    prevMatchesRef.current = current;
  }, [matchesByCategory, lang, toast]);

  // Update selected button when searching
  useEffect(() => {
    if (matchingStatus.isSearching && matchingStatus.category) {
      const uiCategory = reverseCategoryKeyMap[matchingStatus.category];
      if (uiCategory) setSelectedButton(uiCategory);
    }
  }, [matchingStatus.isSearching, matchingStatus.category, reverseCategoryKeyMap]);

  // Load search requests
  useEffect(() => {
    loadSearchRequestsByCategory();
  }, [loadSearchRequestsByCategory]);

  // Poll matching status when searching
  useEffect(() => {
    if (!matchingStatus.isSearching) return;
    const interval = setInterval(() => {
      loadMatchingStatus();
      loadMatchesByCategories();
    }, 5000);
    return () => clearInterval(interval);
  }, [matchingStatus.isSearching, loadMatchingStatus, loadMatchesByCategories]);

  // Animate list visibility on filter change
  useEffect(() => {
    setIsListVisible(false);
    const timer = setTimeout(() => {
      setIsListVisible(true);
    }, 400);
    return () => clearTimeout(timer);
  }, [timeFilter]);

  useEffect(() => {
    return () => {
      if (autoRematchTimerRef.current) {
        clearTimeout(autoRematchTimerRef.current);
        autoRematchTimerRef.current = null;
      }
    };
  }, []);

  // Update new match indicators
  useEffect(() => {
    setNewMatchIndicators({
      clients: (matchesByCategory.business_clients || []).some(m => m.my_status === "pending"),
      startup: (matchesByCategory.startup || []).some(m => m.my_status === "pending"),
      job: (matchesByCategory.job || []).some(m => m.my_status === "pending"),
      other: (matchesByCategory.other || []).some(m => m.my_status === "pending"),
      friends: (matchesByCategory.friends || []).some(m => m.my_status === "pending"),
    });
  }, [matchesByCategory]);

  // Users by day
  const usersByDay = useMemo(() => {
    const map = {};
    invitedUsers.forEach(user => {
      const date = new Date(user.created_at).toISOString().split('T')[0];
      if (!map[date]) map[date] = [];
      map[date].push(user);
    });
    return map;
  }, [invitedUsers]);

  // Period counts
  const periodCounts = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    const monthAgo = new Date(now);
    monthAgo.setMonth(now.getMonth() - 1);
    const yearAgo = new Date(now);
    yearAgo.setFullYear(now.getFullYear() - 1);
    return {
      all: invitedUsers.length,
      week: invitedUsers.filter(u => new Date(u.created_at) >= weekAgo).length,
      month: invitedUsers.filter(u => new Date(u.created_at) >= monthAgo).length,
      year: invitedUsers.filter(u => new Date(u.created_at) >= yearAgo).length,
      uniqueDays: Object.keys(usersByDay).length
    };
  }, [invitedUsers, usersByDay]);

  // Filtered invited users
  const filteredInvitedUsers = useMemo(() => {
    if (timeFilter === "all") return invitedUsers;
    const now = new Date();
    let cutoffDate;
    if (timeFilter === "week") {
      cutoffDate = new Date(now);
      cutoffDate.setDate(now.getDate() - 7);
    } else if (timeFilter === "month") {
      cutoffDate = new Date(now);
      cutoffDate.setMonth(now.getMonth() - 1);
    } else if (timeFilter === "year") {
      cutoffDate = new Date(now);
      cutoffDate.setFullYear(now.getFullYear() - 1);
    }
    return invitedUsers.filter(user => new Date(user.created_at) >= cutoffDate);
  }, [invitedUsers, timeFilter]);

  // Flat match data for the Matches folder
  const flatMatchData = useMemo(() => {
    const allRequests = Object.entries(searchRequestsByCategory)
      .flatMap(([key, reqs]) => {
        if (!Array.isArray(reqs)) return [];
        const cat = CATEGORY_META.find((meta) => meta.backendKey === key) || null;
        return reqs.map((sr) => ({ ...sr, _cat: cat }));
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const allMatchesFlat = CATEGORY_META.flatMap(cat => {
      const ms = matchesByCategory[cat.backendKey] || [];
      return ms.map(m => ({ ...m, _cat: cat }));
    }).filter((m) => {
      const id = m.match_id ?? m.id;
      return id == null || !hiddenMatchIds.includes(id);
    });

    const isIntuitiveMatch = (m) => {
      if (!m || typeof m !== "object") return false;
      if (m.match_mode === "intuitive_discovery") return true;
      if (m.is_intuitive_discovery === true) return true;
      const source = m.match_analysis && typeof m.match_analysis === "object"
        ? m.match_analysis.source
        : null;
      return source === "intuitive_discovery_about_only";
    };

    const intuitiveMatches = allMatchesFlat
      .filter((m) => isIntuitiveMatch(m))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const requestBasedMatchesFlat = allMatchesFlat.filter(
      (m) => !isIntuitiveMatch(m)
    );

    const allRequestIds = new Set(allRequests.map(r => r.id));
    const orphanRequestMatches = requestBasedMatchesFlat
      .filter(m => m.search_request_id == null || !allRequestIds.has(m.search_request_id))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const newIds = new Set();
    allMatchesFlat.forEach(m => {
      const id = m.match_id ?? m.id;
      if (id != null && !seenMatchIdsRef.current.has(id)) {
        newIds.add(id);
        seenMatchIdsRef.current.add(id);
      }
    });

    return {
      allRequests,
      allMatchesFlat,
      requestBasedMatchesFlat,
      intuitiveMatches,
      orphanRequestMatches,
      newIds,
    };
  }, [searchRequestsByCategory, matchesByCategory, hiddenMatchIds]);

  /** Shared About + email verification block (mobile hero + desktop main column). */
  const renderAboutEmailSection = () => (
    <>
      <div style={{ marginTop: isMobile ? 12 : 0 }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "space-between",
          marginBottom: 8,
          gap: isMobile ? 8 : 10,
          flexWrap: "nowrap",
        }}>
          <span style={{
            fontSize: 11,
            letterSpacing: "0.1em",
            color: themeVars.fg, opacity: 0.45,
            textTransform: "uppercase",
            fontWeight: 600,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}>
            {lang === "fr" ? "À propos de moi" : "About me"}
          </span>
          {profileInfo.about && (
            <button
              onClick={() => navigate("/chat/ai", { state: { prefill: "change_about_me" } })}
              style={{
                background: "none", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
                color: "#667eea", fontSize: 13, opacity: 0.85, padding: 0,
                fontFamily: "inherit",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M11 2l3 3-9 9H2v-3l9-9z" stroke="#667eea" strokeWidth="1.4" strokeLinejoin="round" />
              </svg>
              {lang === "fr" ? "Modifier" : "Edit"}
            </button>
          )}
        </div>

        {profileInfo.about ? (
          <p style={{
            margin: 0,
            fontSize: 14, lineHeight: 1.7,
            color: themeVars.fg, opacity: 0.75,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
            {profileInfo.about}
          </p>
        ) : (
          <button
            onClick={() => navigate("/chat/ai", { state: { prefill: "change_about_me" } })}
            style={{
              background: "none",
              border: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : LH_PROFILE.dashedBorder}`,
              borderRadius: 10,
              padding: "12px",
              width: "100%",
              color: themeVars.fg, opacity: 0.5,
              fontSize: 14, cursor: "pointer",
              textAlign: "center",
              fontFamily: "inherit",
            }}
          >
            {lang === "fr" ? "+ Ajouter une description" : "+ Add description"}
          </button>
        )}
      </div>

      {!profileInfo.email_verified && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          borderRadius: 10,
          background: isDark ? "rgba(233,189,12,0.07)" : LH_PROFILE.warningBg,
          border: `1px solid ${isDark ? "rgba(233,189,12,0.18)" : LH_PROFILE.warningBorder}`,
          flexWrap: "wrap",
        }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
            <rect x="1" y="3" width="14" height="10" rx="2" stroke={themeVars.warning} strokeWidth="1.4" />
            <path d="M1 6l7 4 7-4" stroke={themeVars.warning} strokeWidth="1.4" />
          </svg>

          {!emailVerifyExpanded ? (
            <>
              <span style={{ fontSize: 13, color: themeVars.warning, flex: 1, minWidth: 0 }}>
                {lang === "fr" ? "Email non vérifié" : "Email not verified"}
              </span>
              <button
                onClick={() => setEmailVerifyExpanded(true)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: themeVars.warning, fontSize: 13,
                  textDecoration: "underline", textUnderlineOffset: 2,
                  padding: 0,
                  fontFamily: "inherit",
                }}
              >
                {lang === "fr" ? "Vérifier →" : "Verify →"}
              </button>
            </>
          ) : !emailCodeSent ? (
            <>
              <span style={{ fontSize: 13, color: themeVars.warning, flex: 1, minWidth: 0 }}>
                {lang === "fr" ? "Envoyer un code à votre email ?" : "Send a code to your email?"}
              </span>
              <button
                onClick={async () => {
                  const ok = await sendEmailVerificationCode();
                  if (ok) setEmailCodeSent(true);
                }}
                disabled={emailSending}
                style={{
                  background: isDark ? "rgba(233,189,12,0.15)" : LH_PROFILE.warningBtnBg,
                  border: `1px solid ${isDark ? "rgba(233,189,12,0.3)" : LH_PROFILE.warningBtnBorder}`,
                  borderRadius: 7,
                  padding: "6px 14px",
                  color: themeVars.warning,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: emailSending ? "not-allowed" : "pointer",
                  opacity: emailSending ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                {emailSending
                  ? (lang === "fr" ? "Envoi..." : "Sending...")
                  : (lang === "fr" ? "Envoyer" : "Send code")}
              </button>
              <button
                onClick={() => setEmailVerifyExpanded(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: themeVars.fg, opacity: 0.45,
                  fontSize: 18, lineHeight: 1, padding: "0 4px",
                  fontFamily: "inherit",
                }}
                aria-label="Close"
              >
                ×
              </button>
            </>
          ) : (
            <>
              <input
                autoFocus
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                placeholder={lang === "fr" ? "Code 6 chiffres" : "6-digit code"}
                style={{
                  flex: 1,
                  minWidth: 110,
                  maxWidth: 150,
                  background: isDark ? "rgba(255,255,255,0.06)" : LH_PROFILE.emailInputBg,
                  border: `1px solid ${isDark ? "rgba(233,189,12,0.25)" : LH_PROFILE.warningBorder}`,
                  borderRadius: 7,
                  padding: "6px 10px",
                  color: themeVars.fg,
                  fontSize: 14,
                  outline: "none",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={confirmEmailVerificationCode}
                disabled={emailConfirming || !emailCode.trim()}
                style={{
                  background: isDark ? "rgba(233,189,12,0.15)" : LH_PROFILE.warningBtnBg,
                  border: `1px solid ${isDark ? "rgba(233,189,12,0.3)" : LH_PROFILE.warningBtnBorder}`,
                  borderRadius: 7,
                  padding: "6px 14px",
                  color: themeVars.warning,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: (emailConfirming || !emailCode.trim()) ? "not-allowed" : "pointer",
                  opacity: (emailConfirming || !emailCode.trim()) ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                {emailConfirming
                  ? "..."
                  : (lang === "fr" ? "Valider" : "Confirm")}
              </button>
              <button
                onClick={() => { setEmailVerifyExpanded(false); setEmailCode(""); setEmailCodeSent(false); }}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: themeVars.fg, opacity: 0.45,
                  fontSize: 18, lineHeight: 1, padding: "0 4px",
                  fontFamily: "inherit",
                }}
                aria-label="Close"
              >
                ×
              </button>
            </>
          )}
        </div>
      )}
    </>
  );

  // Loading state
  if (loading) {
    return (
      <div style={{
        maxWidth: 600,
        margin: "40px auto",
        padding: "24px 20px",
        textAlign: "center",
        color: themeVars.fg,
        background: themeVars.card,
        borderRadius: 16,
        boxShadow: "0 4px 20px rgba(0,0,0,.1)",
        width: "90%"
      }}>
        {translations[lang].loading}...
      </div>
    );
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────
  const profileName = profileInfo.name || translations[lang]?.profile || "Profile";
  const profileRole = profileInfo.role || "Profile";
  const profileAboutText = (profileInfo.about || "").trim();
  const allDashboardMatches = Array.isArray(flatMatchData.allMatchesFlat) ? flatMatchData.allMatchesFlat : [];
  const sortDashboardMatches = (matches) => [...matches].sort((a, b) => {
    const aPending = a?.my_status === "pending" ? 1 : 0;
    const bPending = b?.my_status === "pending" ? 1 : 0;
    if (aPending !== bPending) return bPending - aPending;
    return new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
  });
  /** One match per carousel page, in group order (see `finalizeDashboardMatchPageOrder` for user-facing page order). */
  const buildSingleMatchPages = (groups) =>
    groups.flatMap((group) => {
      const matches = sortDashboardMatches(Array.isArray(group.matches) ? group.matches : []);
      if (matches.length === 0) {
        // Keep search-request groups visible even before any matches arrive —
        // the card renders the "No matches for this request yet" placeholder.
        if (group.kind === "request") {
          return [{
            ...group,
            pageIndex: 0,
            matchPageCount: 1,
            totalMatches: 0,
            matches: [],
          }];
        }
        return [];
      }
      return matches.map((match, pageIndex) => ({
        ...group,
        pageIndex,
        matchPageCount: matches.length,
        totalMatches: matches.length,
        matches: [match],
      }));
    });
  const dashboardRequestGroups = [
    ...flatMatchData.allRequests
      .map((request) => {
        const requestMatches = sortDashboardMatches(
          flatMatchData.requestBasedMatchesFlat.filter((match) => match.search_request_id === request.id)
        );
        return {
          id: `request-${request.id}`,
          kind: "request",
          request,
          title: request.request_text || (lang === "fr" ? "Demande de recherche" : "Search request"),
          subtitle: request.created_at ? formatDate(request.created_at) : "",
          category: request._cat,
          totalMatches: requestMatches.length,
          matches: requestMatches,
        };
      })
      .sort((a, b) => {
        // Requests with at least one pending match (Accept/Decline buttons) come first;
        // remaining requests stay in chronological order (newest first).
        const aPending = a.matches.some((m) => m?.my_status === "pending") ? 1 : 0;
        const bPending = b.matches.some((m) => m?.my_status === "pending") ? 1 : 0;
        if (aPending !== bPending) return bPending - aPending;
        return new Date(b.request?.created_at || 0) - new Date(a.request?.created_at || 0);
      }),
    ...(flatMatchData.orphanRequestMatches.length > 0 ? [{
      id: "other-matches",
      kind: "orphan",
      title: lang === "fr" ? "Autres correspondances" : "Other matches",
      subtitle: "",
      totalMatches: flatMatchData.orphanRequestMatches.length,
      matches: sortDashboardMatches(flatMatchData.orphanRequestMatches),
    }] : []),
  ];
  const dashboardDiscoveryGroups = [{
    id: "intuitive-discovery",
    kind: "discovery",
    title: lang === "fr" ? "Découverte intuitive" : "Intuitive discovery",
    subtitle: lang === "fr" ? "Basé sur About" : "Based on About",
    totalMatches: flatMatchData.intuitiveMatches.length,
    matches: sortDashboardMatches(flatMatchData.intuitiveMatches),
  }];
  const dashboardMatchGroups = finalizeDashboardMatchPageOrder(
    activeMatchesTab === "discovery"
      ? buildSingleMatchPages(dashboardDiscoveryGroups)
      : buildSingleMatchPages(dashboardRequestGroups)
  );
  const dashboardMatchTotalPages = Math.max(1, dashboardMatchGroups.length);
  const dashboardMatchPage = Math.min(matchPage, dashboardMatchTotalPages);
  const dashboardCurrentMatchGroup = dashboardMatchGroups[dashboardMatchPage - 1] || null;
  const goToMatchPage = (newPage) => {
    const target = Math.max(1, Math.min(dashboardMatchTotalPages, newPage));
    if (target === dashboardMatchPage) return;
    matchPageDirectionRef.current = target > dashboardMatchPage ? "forward" : "backward";
    setMatchPage(target);
  };
  activePaginationRef.current = {
    page: dashboardMatchPage,
    totalPages: dashboardMatchTotalPages,
    setPage: goToMatchPage,
  };
  const dashboardCompletedInvites = invitedUsers.filter((user) => user.profile_completed).length;
  const dashboardActiveEventIds = Array.isArray(profileInfo.active_event_ids)
    ? profileInfo.active_event_ids
    : (profileInfo.active_event_id ? [profileInfo.active_event_id] : []);
  const dashboardAcceptedMatches = allDashboardMatches.filter((match) => match?.my_status === "accepted").length;
  const dashboardMeetingCount = Math.max(dashboardAcceptedMatches, 0);
  const dashboardRating = profileInfo.rating || profileInfo.average_rating || 4.8;
  const dashboardBalance = balanceLoading
    ? "..."
    : (balance !== null && Number.isFinite(Number(balance)) ? "€" + Number(balance).toFixed(2) : "€0.00");
  const dashboardLocation = [
    profileInfo.city || profileInfo.location || "Vienna",
    profileInfo.country_code || profileInfo.country || "AT",
  ].filter(Boolean).join(" • ");
  const dashboardJoined = profileInfo.created_at
    ? "Joined " + formatDate(profileInfo.created_at)
    : "Joined Apr 2026";
  /* Temporarily hidden — pill tags under About (restore when needed)
  const dashboardTags = [
    profileRole && profileRole !== "Profile" ? profileRole : "Founder",
    profileInfo.request ? "Active search" : "Marketplace",
    profileInfo.city || "Vienna",
    matchingStatus.isSearching ? "Matching now" : "Co-founder search",
  ].filter(Boolean).slice(0, 4);
  */

  const getInitials = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "U";
    const parts = raw.split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase();
  };

  const getAvatarGradient = (value) => {
    const palette = [
      "linear-gradient(135deg, #ec4899, #6366f1)",
      "linear-gradient(135deg, #3b82f6, #06b6d4)",
      "linear-gradient(135deg, #f97316, #b45309)",
      "linear-gradient(135deg, #14b8a6, #0891b2)",
      "linear-gradient(135deg, #8b5cf6, #db2777)",
    ];
    const raw = String(value || "");
    const sum = raw.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return palette[sum % palette.length];
  };

  const getMatchName = (match) =>
    match?.other_user_name || match?.user_name || match?.name || (match?.id ? "Match " + match.id : "New match");

  const getMatchRole = (match) =>
    match?.other_user_role || match?.role || match?.category ||
    (lang === "fr" ? match?._cat?.fr : match?._cat?.en) || "Potential connection";

  const getMatchReason = (match) => {
    if (!match) return "";
    const analysis = match.match_analysis;
    const analysisReason = typeof analysis === "string"
      ? analysis
      : (analysis?.reason || analysis?.summary || analysis?.explanation || analysis?.match_reason || "");
    return (
      (lang === "ru" ? match.match_reason_ru : "") ||
      (lang === "fr" ? match.match_reason_fr : match.match_reason_en) ||
      match.match_reason ||
      analysisReason ||
      ""
    );
  };

  const getMatchScore = (match) => {
    const score = match?.compatibility_score ?? match?.score ?? match?.match_score;
    return score != null && score !== "" ? Number(score) : null;
  };

  const copyDashboardInvite = async () => {
    const inviteUrl = generateInviteUrl(qrCodeData?.personal_qr_code, qrCodeData);
    if (!inviteUrl) {
      toast(lang === "fr" ? "QR indisponible" : "QR is not ready", "warning");
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast(lang === "fr" ? "Lien copié" : "Invite link copied", "success");
    } catch {
      toast(lang === "fr" ? "Échec de la copie" : "Copy failed", "error");
    }
  };

  const downloadDashboardQr = () => {
    if (!qrCodeDataURL || !qrCodeData?.personal_qr_code) {
      toast(lang === "fr" ? "QR indisponible" : "QR is not ready", "warning");
      return;
    }
    const link = document.createElement("a");
    link.href = qrCodeDataURL;
    link.download = `qr-code-${qrCodeData.personal_qr_code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast(lang === "fr" ? "QR téléchargé" : "QR code downloaded", "success");
  };

  const inviteQrTitle = lang === "fr" ? "Votre QR d'invitation" : "Your invite QR";
  const inviteQrShareText = lang === "fr"
    ? "Partagez pour inviter des personnes directement dans votre reseau."
    : "Share to invite people directly to your network.";

  const openInviteQrModal = () => {
    if (!qrCodeDataURL) {
      loadPermanentQRCode();
      return;
    }
    copyDashboardInvite();
    setInviteQrModalOpen(true);
  };

  const handleInviteQrModalImageClick = () => {
    if (adminRoute) navigate(`/${adminRoute}/login`);
  };

  const NeoIcon = ({ name, className = "", style = {} }) => {
    const common = {
      className,
      style,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
    };
    if (name === "users") return <svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    if (name === "send") return <svg {...common}><path d="M22 2 11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
    if (name === "zap") return <svg {...common}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
    if (name === "ticket") return <svg {...common}><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><line x1="9" y1="9" x2="9" y2="15"/></svg>;
    if (name === "trend") return <svg {...common}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
    if (name === "spark") return <svg {...common}><path d="M12 2v6"/><path d="M12 16v6"/><path d="M2 12h6"/><path d="M16 12h6"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="m19.07 4.93-4.24 4.24"/><path d="m9.17 14.83-4.24 4.24"/></svg>;
    if (name === "star") return <svg {...common}><polygon points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5 12 2"/></svg>;
    if (name === "calendar") return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>;
    if (name === "calendar-plus") return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/><path d="M12 14v4"/><path d="M10 16h4"/></svg>;
    if (name === "pulse") return <svg {...common}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
    if (name === "mail") return <svg {...common}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/></svg>;
    if (name === "location") return <svg {...common}><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0Z"/><circle cx="12" cy="10" r="3"/></svg>;
    if (name === "copy") return <svg {...common}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
    if (name === "download") return <svg {...common}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
    if (name === "edit") return <svg {...common}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>;
    if (name === "chevron-left") return <svg {...common}><path d="m15 18-6-6 6-6"/></svg>;
    if (name === "x") return <svg {...common}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
    if (name === "more-vertical") return <svg {...common}><circle cx="12" cy="5" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="19" r="1.5" fill="currentColor" stroke="none"/></svg>;
    if (name === "archive") return <svg {...common}><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5" rx="1"/><line x1="10" y1="12" x2="14" y2="12"/></svg>;
    if (name === "trash") return <svg {...common}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>;
    return <svg {...common}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"/></svg>;
  };

  const renderDashboardAvatar = () => {
    const hasCustomAvatar = avatarPreview && avatarPreview !== defaultAvatar;
    return (
      <div className={"neo-avatar" + (isAvatarLoading ? " loading" : "") + (avatarSuccess ? " success" : "")}>
        {hasCustomAvatar ? (
          <img
            src={avatarPreview}
            alt="Avatar"
            onError={() => {
              setAvatarPreview(null);
              localStorage.removeItem("userAvatar");
            }}
          />
        ) : (
          <NeoIcon name="message" className="neo-avatar-symbol" />
        )}
        <div className="neo-avatar-edit">
          <img src={editIcon} alt="" />
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          id="avatar-upload-neo"
          onChange={handleAvatarUpload}
        />
        <label htmlFor="avatar-upload-neo" aria-label="Upload avatar" />
      </div>
    );
  };

  const renderEmailBanner = () => {
    if (profileInfo.email_verified) return null;
    const emailStage = !emailVerifyExpanded ? "collapsed" : !emailCodeSent ? "sending" : "sent";
    const closeEmailFlow = () => {
      setEmailVerifyExpanded(false);
      setEmailCode("");
      setEmailCodeSent(false);
    };
    return (
      <>
      <div className="neo-banner neo-banner-chip">
        <span>{lang === "fr" ? "Matching limité" : "Limited matching"}</span>
      </div>
      <div className="neo-banner-row" data-stage={emailStage}>
        <div className="neo-banner neo-banner-email">
          <div className="neo-banner-left neo-banner-fade" key={`email-${emailStage}`}>
            <NeoIcon name="mail" />
            {emailStage === "collapsed" && (
              <span>{lang === "fr" ? "Email non vérifié" : "Email not verified"}</span>
            )}
            {emailStage === "sending" && (
              <span>{lang === "fr" ? "Envoyer un code ?" : "Send a code?"}</span>
            )}
            {emailStage === "sent" && (
              <input
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                placeholder={lang === "fr" ? "Code 6 chiffres" : "6-digit code"}
                className="neo-banner-input"
              />
            )}
          </div>
        </div>
        <div className="neo-banner neo-banner-verify neo-banner-fade" key={`verify-${emailStage}`}>
          {emailStage === "collapsed" && (
            <button className="neo-banner-action" onClick={() => setEmailVerifyExpanded(true)}>
              {lang === "fr" ? "Vérifier →" : "Verify →"}
            </button>
          )}
          {emailStage === "sending" && (
            <button
              className="neo-banner-action"
              disabled={emailSending}
              onClick={async () => {
                const ok = await sendEmailVerificationCode();
                if (ok) setEmailCodeSent(true);
              }}
            >
              {emailSending ? (lang === "fr" ? "Envoi..." : "Sending...") : (lang === "fr" ? "Envoyer" : "Send code")}
            </button>
          )}
          {emailStage === "sent" && (
            <button
              className="neo-banner-action"
              disabled={emailConfirming || !emailCode.trim()}
              onClick={confirmEmailVerificationCode}
            >
              {emailConfirming ? "..." : (lang === "fr" ? "Valider" : "Confirm")}
            </button>
          )}
        </div>
        {emailStage !== "collapsed" && (
          <div className="neo-banner neo-banner-close neo-banner-fade" key={`close-${emailStage}`}>
            <button className="neo-icon-action" onClick={closeEmailFlow}>×</button>
          </div>
        )}
      </div>
      </>
    );
  };

  const renderDashboardRequestMatch = (match, index) => {
    const realName = getMatchName(match);
    const score = getMatchScore(match);
    const matchDateTime = formatDateTime(match?.created_at);
    const reason = getMatchReason(match);
    const isPending = match?.my_status === "pending";
    const isAccepted = match?.my_status === "accepted";
    const hasChat = !!match?.chat_id;
    const isActive = match?.status === "active" || match?.both_accepted === true;
    const nameRevealed = isActive && !!match?.other_user_name;
    const hiddenLabel = lang === "fr" ? "Nouvel utilisateur" : "New user";
    const displayName = nameRevealed ? realName : hiddenLabel;
    const avatarSeed = nameRevealed ? realName : "?";
    return (
      <div className="neo-request-match" key={match?.match_id || match?.id || index}>
        <div className="neo-request-match-header">
          <div className="neo-request-match-avatar" style={{ background: getAvatarGradient(avatarSeed) }}>
            {nameRevealed ? getInitials(realName) : "?"}
          </div>
          <div className="neo-request-match-main">
            <div className="neo-request-match-top">
              <span className="neo-request-match-name">{displayName}</span>
              {score != null && <span className="neo-request-match-score">{score}%</span>}
            </div>
            {matchDateTime && <div className="neo-request-match-role">{matchDateTime}</div>}
          </div>
          {!isPending && (
            <div className="neo-request-match-actions solo">
              {hasChat ? (
                <button onClick={() => { window.location.href = `/chat/${match.chat_id}`; }}>Chat</button>
              ) : (
                <span>{isAccepted ? "Confirmed" : "Decided"}</span>
              )}
            </div>
          )}
        </div>
        {reason && (
          <div
            className="neo-request-match-reason"
            data-match-reason-scroll
            onScroll={(e) => syncMatchReasonScrollEdges(e.currentTarget)}
            ref={(el) => {
              if (el) requestAnimationFrame(() => syncMatchReasonScrollEdges(el));
            }}
          >
            {reason}
          </div>
        )}
        {isPending && (
          <div className="neo-request-match-actions">
            <button className="accept" onClick={() => confirmMatch({ ...match, id: match.id ?? match.match_id }, true)}>Accept</button>
            <button className="danger" onClick={() => openRejectModal({ ...match, id: match.id ?? match.match_id })}>Decline</button>
          </div>
        )}
      </div>
    );
  };

  const renderDashboardMatchRequests = () => {
    const isDiscoveryTab = activeMatchesTab === "discovery";
    const footerActionLabel = isDiscoveryTab
      ? (!profileAboutText
          ? (lang === "fr" ? "Ajouter About" : "Add About")
          : intuitiveDiscoveryLoading
            ? (lang === "fr" ? "Recherche..." : "Searching...")
            : (lang === "fr" ? "Relancer discovery" : "Run discovery"))
      : (lang === "fr" ? "Nouvelle recherche" : "New search");
    const handleFooterAction = () => {
      if (!isDiscoveryTab) {
        navigate("/chat/ai", { state: { prefill: "new_match" } });
        return;
      }
      if (!profileAboutText) {
        navigate("/chat/ai", { state: { prefill: "change_about_me" } });
        return;
      }
      handleIntuitiveDiscovery();
    };

    if (!dashboardCurrentMatchGroup) {
      if (isDiscoveryTab) {
        return (
          <div className="neo-empty neo-request-empty">
            <span>
              {profileAboutText
                ? (lang === "fr" ? "Aucun match discovery pour le moment" : "No discovery matches yet")
                : (lang === "fr" ? "Ajoutez About pour lancer discovery" : "Add About to start discovery")}
            </span>
            <button
              type="button"
              className="neo-match-button"
              onClick={handleFooterAction}
              disabled={intuitiveDiscoveryLoading}
            >
              {footerActionLabel}
            </button>
          </div>
        );
      }
      return (
        <div className="neo-empty neo-request-empty">
          <span>{lang === "fr" ? "Aucune demande de recherche pour le moment" : "No search requests yet"}</span>
          <button className="neo-match-button" onClick={() => navigate("/chat/ai", { state: { prefill: "new_match" } })}>
            {lang === "fr" ? "Lancer une recherche" : "Start a search"}
          </button>
        </div>
      );
    }

    const matchesToShow = dashboardCurrentMatchGroup.matches;
    const isDiscoveryGroup = dashboardCurrentMatchGroup.kind === "discovery";
    const totalMatches = dashboardCurrentMatchGroup.totalMatches ?? matchesToShow.length;
    const pageInGroup = (dashboardCurrentMatchGroup.pageIndex || 0) + 1;
    const groupPageCount = dashboardCurrentMatchGroup.matchPageCount || 1;
    const hasGroupPages = groupPageCount > 1;
    const noMatchesText = isDiscoveryGroup
      ? (profileAboutText
          ? (lang === "fr" ? "Aucun match discovery pour le moment" : "No discovery matches yet")
          : (lang === "fr" ? "Ajoutez About pour lancer discovery" : "Add About to start discovery"))
      : (matchingStatus.isSearching
          ? (lang === "fr" ? "Recherche en cours..." : "Searching...")
          : (lang === "fr" ? "Aucun match pour cette demande" : "No matches for this request yet"));
    const slideClass = matchPageDirectionRef.current === "backward" ? " slide-from-left" : " slide-from-right";
    return (
      <div className="neo-request-shell" ref={matchesBlockRef}>
        <div className={"neo-request-card" + slideClass} key={`match-page-${dashboardMatchPage}`}>
          <div className="neo-request-header">
            <div className="neo-request-title-wrap">
              <div className="neo-request-title">{dashboardCurrentMatchGroup.title}</div>
              <div className="neo-request-subtitle">
                {dashboardCurrentMatchGroup.category && (
                  <span style={{
                    color: dashboardCurrentMatchGroup.category.color,
                    borderColor: `${dashboardCurrentMatchGroup.category.color}66`,
                    background: `${dashboardCurrentMatchGroup.category.color}22`,
                  }}>
                    {lang === "fr" ? dashboardCurrentMatchGroup.category.fr : dashboardCurrentMatchGroup.category.en}
                  </span>
                )}
                {dashboardCurrentMatchGroup.subtitle}
              </div>
            </div>
            {totalMatches > 1 && (
              <div className="neo-request-count">
                <strong>{pageInGroup}/{groupPageCount}</strong>
              </div>
            )}
          </div>

          {dashboardCurrentMatchGroup.kind === "request" && dashboardCurrentMatchGroup.request?.id && (() => {
            const req = dashboardCurrentMatchGroup.request;
            const isArchived = req.status === "archived" || req.is_active === false;
            const isArchivingNow = !!archivingRequestIds[req.id];
            const isDeleting = !!deletingRequestIds[req.id];
            const isMenuOpen = openMenuRequestId === req.id;
            const statusLabelMap = {
              active: lang === "fr" ? "Active" : lang === "ru" ? "Активен" : "Active",
              waiting_for_match: lang === "fr" ? "En attente" : lang === "ru" ? "Ожидание" : "Waiting",
              archived: lang === "fr" ? "Archivée" : lang === "ru" ? "В архиве" : "Archived",
            };
            const statusKey = isArchived ? "archived" : (req.status || "active");
            const statusColor = statusKey === "archived" ? "#9ca3af" : (statusKey === "waiting_for_match" ? "#f59e0b" : "#10b981");
            return (
              <>
                <div className={"neo-request-menu-wrap" + (isMenuOpen ? " neo-request-menu-wrap--open" : "")}>
                  <button
                    type="button"
                    className="neo-request-dots"
                    onClick={(e) => { e.stopPropagation(); setOpenMenuRequestId(isMenuOpen ? null : req.id); }}
                    aria-label={lang === "fr" ? "Options" : lang === "ru" ? "Действия" : "Options"}
                  >
                    <NeoIcon name="more-vertical" />
                  </button>
                  {isMenuOpen && (
                    <div className="neo-request-dropdown" onClick={(e) => e.stopPropagation()}>
                      {lang !== "ru" && (
                        <button
                          type="button"
                          className="neo-dropdown-item"
                          onClick={() => { setOpenMenuRequestId(null); navigate("/chat/ai", { state: { prefill: "edit_request", requestText: req.request_text || dashboardCurrentMatchGroup.title || "", requestId: req.id } }); }}
                        >
                          <NeoIcon name="edit" />
                          {lang === "fr" ? "Modifier" : "Edit"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="neo-dropdown-item"
                        disabled={isArchivingNow}
                        onClick={() => { setOpenMenuRequestId(null); handleArchiveSearchRequest(req); }}
                      >
                        <NeoIcon name="archive" />
                        {isArchived
                          ? (lang === "fr" ? "Réactiver" : lang === "ru" ? "Вернуть" : "Reactivate")
                          : (lang === "fr" ? "Archiver" : lang === "ru" ? "В архив" : "Archive")}
                      </button>
                      <button
                        type="button"
                        className="neo-dropdown-item neo-dropdown-item--delete"
                        disabled={isDeleting}
                        onClick={() => { setOpenMenuRequestId(null); handleDeleteSearchRequest(req); }}
                      >
                        <NeoIcon name="trash" />
                        {lang === "fr" ? "Supprimer" : lang === "ru" ? "Удалить" : "Delete"}
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", margin: "10px 0 4px" }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    fontSize: 11, fontWeight: 700, color: statusColor,
                    border: `1px solid ${statusColor}55`, background: `${statusColor}1a`,
                    borderRadius: 999, padding: "3px 10px",
                  }}>
                    ● {statusLabelMap[statusKey] || statusKey}
                  </span>
                </div>
              </>
            );
          })()}

          {matchesToShow.length > 0 ? (
            <div className="neo-request-match-list">
              {matchesToShow.map(renderDashboardRequestMatch)}
            </div>
          ) : (
            <div className="neo-request-no-matches">
              <span>{noMatchesText}</span>
              {isDiscoveryGroup && (
                <button onClick={handleFooterAction} disabled={intuitiveDiscoveryLoading}>
                  {footerActionLabel}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="neo-request-footer">
          <div className="neo-request-footer-inner">
            <button
              type="button"
              className="neo-request-page-btn"
              aria-label={lang === "fr" ? "Page précédente" : "Previous match"}
              onClick={() => goToMatchPage(dashboardMatchPage - 1)}
              disabled={dashboardMatchPage <= 1}
            >
              ←
            </button>
            <span className="neo-request-page-indicator">{dashboardMatchPage} / {dashboardMatchTotalPages}</span>
            <button
              type="button"
              className="neo-request-page-btn"
              aria-label={lang === "fr" ? "Page suivante" : "Next match"}
              onClick={() => goToMatchPage(dashboardMatchPage + 1)}
              disabled={dashboardMatchPage >= dashboardMatchTotalPages}
            >
              →
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEventItem = (eventItem) => {
    const eventKey = eventItem.id || eventItem.event_id;
    const isExpired = isEventMatchingExpired(eventItem);
    const profileActiveIds = Array.isArray(profileInfo.active_event_ids)
      ? profileInfo.active_event_ids
      : (profileInfo.active_event_id ? [profileInfo.active_event_id] : []);
    const hasProfileScope = profileActiveIds.length > 0 || profileInfo.active_event_id != null;
    const fallbackActive = hasProfileScope
      ? profileActiveIds.some((id) => String(id) === String(eventKey))
      : (typeof eventItem.is_active === "boolean" ? eventItem.is_active : false);
    const isActive = isExpired ? false : (localToggleState[eventKey] ?? fallbackActive);
    const historyLabel = lang === "fr" ? "En historique" : lang === "ru" ? "В истории" : "In history";
    return (
      <div className="neo-event-item" key={eventKey || eventItem.title}>
        <div className="neo-event-icon">{getInitials(eventItem.title || "Event")}</div>
        <button
          className="neo-event-info"
          onClick={() => navigate("/events/" + (eventItem.slug || eventItem.event_id || eventItem.id))}
        >
          <span className="neo-event-title">{eventItem.title || (lang === "fr" ? "Événement lié" : "Linked event")}</span>
          <span className="neo-event-date">
            {formatDate(eventItem.event_date || eventItem.created_at)}{eventItem.location ? " · " + eventItem.location : ""}
          </span>
        </button>
        <div className="neo-event-right">
          {isExpired ? (
            <span className="neo-event-history-badge" aria-label={historyLabel}>{historyLabel}</span>
          ) : (
            <button
              type="button"
              className={"neo-toggle" + (isActive ? " active" : "")}
              aria-pressed={isActive}
              disabled={activeEventUpdating}
              onClick={() => {
                if (activeEventUpdating) return;
                const currentIds = Array.isArray(profileInfo.active_event_ids)
                  ? profileInfo.active_event_ids
                  : (profileInfo.active_event_id ? [profileInfo.active_event_id] : []);
                const nextEventIds = isActive
                  ? currentIds.filter((id) => String(id) !== String(eventKey))
                  : [...currentIds, eventKey];
                updateActiveEvent(nextEventIds);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const statCards = [
    {
      title: "Invited",
      value: invitedUsers.length,
      sub: dashboardCompletedInvites + " completed",
      icon: "send",
      tailIcon: "trend",
      onClick: () => {
        setTimeFilter("all");
        setTimeout(() => {
          scrollToProfileSection(invitedSectionRef, "smooth");
        }, 50);
      },
    },
    {
      title: "Matches",
      value: allDashboardMatches.length,
      sub: matchingStatus.isSearching ? "Searching now" : "View matches →",
      icon: "zap",
      tailIcon: "spark",
      onClick: () => {
        setActiveMatchesTab("request");
        setMatchPage(1);
        setTimeout(() => {
          scrollToProfileSection(matchesSectionRef, "smooth");
        }, 50);
      },
    },
    {
      title: "Events",
      value: eventRegistrations.length,
      sub: dashboardActiveEventIds.length + " linked now",
      icon: "ticket",
      tailIcon: "calendar-plus",
      onClick: () => window.location.href = "/events",
    },
    // {
    //   title: "Rating",
    //   value: dashboardRating,
    //   sub: "from " + dashboardMeetingCount + " meetings",
    //   icon: "pulse",
    //   tailIcon: null,
    // },
  ];

  const bgOrbs = [
    { left: 12, top:  8, size: 340, hue: isDark ? "rgba(139,92,246,.30)"  : "rgba(109,74,219,.09)", dur: 22, delay:   0, path: 1 },
    { left: 78, top:  6, size: 270, hue: isDark ? "rgba(34,211,238,.22)"  : "rgba(109,74,219,.07)", dur: 28, delay:  -8, path: 2 },
    { left: 55, top: 72, size: 390, hue: isDark ? "rgba(168,85,247,.20)"  : "rgba(168,85,247,.08)", dur: 35, delay: -14, path: 3 },
    { left:  4, top: 52, size: 210, hue: isDark ? "rgba(56,189,248,.18)"  : "rgba(109,74,219,.06)", dur: 26, delay:  -5, path: 4 },
    { left: 87, top: 58, size: 290, hue: isDark ? "rgba(139,92,246,.22)"  : "rgba(109,74,219,.07)", dur: 32, delay: -18, path: 5 },
    { left: 38, top: 38, size: 185, hue: isDark ? "rgba(34,211,238,.16)"  : "rgba(109,74,219,.05)", dur: 20, delay: -10, path: 6 },
    { left: 63, top: 18, size: 230, hue: isDark ? "rgba(168,85,247,.18)"  : "rgba(168,85,247,.06)", dur: 30, delay:  -3, path: 1 },
  ];

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .neo-profile, .neo-profile * { box-sizing: border-box; }
        .neo-profile {
          --neo-bg: ${isDark ? "#060d1f" : LH_PROFILE.cssVar.bg};
          --neo-text: ${isDark ? "#e6eaf5" : LH_PROFILE.cssVar.text};
          --neo-heading: ${isDark ? "#ffffff" : LH_PROFILE.cssVar.heading};
          --neo-soft: ${isDark ? "#c3c9dc" : LH_PROFILE.cssVar.soft};
          --neo-muted: ${isDark ? "#8b96b8" : LH_PROFILE.cssVar.muted};
          --neo-card: ${isDark ? "rgba(15,25,55,.25)" : LH_PROFILE.cssVar.card};
          --neo-card-strong: ${isDark ? "rgba(20,30,60,.18)" : LH_PROFILE.cssVar.cardStrong};
          --neo-card-nested: ${isDark ? "rgba(20,30,60,.18)" : LH_PROFILE.cssVar.cardNested};
          --neo-line: ${isDark ? "rgba(140,160,220,.14)" : LH_PROFILE.cssVar.line};
          --neo-line-strong: ${isDark ? "rgba(140,160,220,.22)" : LH_PROFILE.cssVar.lineStrong};
          --neo-accent: ${isDark ? "#a855f7" : LH_PROFILE.cssVar.accent};
          --neo-accent-soft: ${isDark ? "rgba(139,92,246,.15)" : LH_PROFILE.cssVar.accentSoft};
          --neo-accent-line: ${isDark ? "rgba(139,92,246,.38)" : LH_PROFILE.cssVar.accentLine};
          --neo-shadow: ${isDark ? "none" : LH_PROFILE.cssVar.shadow};
          --neo-scroll-shadow: ${isDark ? "rgba(0,0,0,0.5)" : LH_PROFILE.cssVar.scrollShadow};
          --neo-profile-media-size: 160px;
          min-height: 100vh;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          overscroll-behavior-x: none;
          color: var(--neo-text);
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          touch-action: manipulation;
          background: ${isDark
            ? "radial-gradient(ellipse 50% 45% at 15% 0%, rgba(139, 92, 246, 0.45), transparent 65%), radial-gradient(ellipse 55% 50% at 90% 5%, rgba(34, 211, 238, 0.32), transparent 65%), radial-gradient(ellipse 45% 40% at 75% 95%, rgba(168, 85, 247, 0.28), transparent 65%), radial-gradient(ellipse 40% 35% at 10% 85%, rgba(56, 189, 248, 0.22), transparent 65%), #060d1f"
            : LH_PROFILE.pageBackground};
          padding: 32px clamp(18px, 3vw, 40px) 46px;
          transition:
            background 0.24s cubic-bezier(0.22, 1, 0.36, 1),
            color 0.18s ease;
        }
        @media (prefers-reduced-motion: no-preference) {
          .neo-profile :where(.neo-card, .neo-stat, .neo-request-card, .neo-match-card, .neo-request-match-reason, .neo-user, .neo-tabs, .neo-tab, .neo-match-tabs, .neo-match-tab, .neo-event-form input, .neo-mini-actions button, .neo-banner, .neo-logout, .neo-see) {
            transition:
              background 0.22s cubic-bezier(0.22,1,0.36,1),
              background-color 0.22s cubic-bezier(0.22,1,0.36,1),
              border-color 0.2s ease,
              color 0.18s ease,
              box-shadow 0.22s ease;
          }
        }
        .neo-shell { width: 100%; max-width: 1400px; margin: 0 auto; overflow-x: hidden; }
        .neo-header { display: grid; grid-template-columns: var(--neo-profile-media-size) minmax(0, 1fr) auto; gap: 28px; align-items: start; margin-bottom: 24px; }
        .neo-avatar-column { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; min-width: 0; }
        .neo-avatar { width: var(--neo-profile-media-size); height: var(--neo-profile-media-size); border-radius: 18px; background: linear-gradient(135deg, #22d3ee 0%, #6366f1 52%, #8b5cf6 100%); display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; box-shadow: 0 10px 40px rgba(99, 102, 241, 0.25), inset 0 0 0 3px rgba(139, 92, 246, 0.68); cursor: pointer; }
        .neo-avatar img:not(.neo-avatar-edit img) { width: 100%; height: 100%; object-fit: cover; display: block; }
        .neo-avatar-symbol { width: 56px; height: 56px; color: white; }
        .neo-avatar input { display: none; }
        .neo-avatar label { position: absolute; inset: 0; cursor: pointer; z-index: 3; }
        .neo-avatar-edit { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.48); opacity: 0; transition: opacity .2s ease; z-index: 2; }
        .neo-avatar-edit img { width: 30px; height: 30px; filter: drop-shadow(0 4px 12px rgba(0,0,0,.4)); }
        .neo-avatar:hover .neo-avatar-edit { opacity: 1; }
        .neo-avatar.loading::after { content: ""; position: absolute; inset: 8px; border: 3px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 14px; animation: neoSpin 1s linear infinite; z-index: 4; }
        @keyframes neoSpin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalPopIn { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes neoQrRipple {
          0% { opacity: .42; transform: scale(.78); }
          72% { opacity: 0; transform: scale(2.08); }
          100% { opacity: 0; transform: scale(2.08); }
        }
        @keyframes neoQrAura {
          0%, 100% { opacity: .22; transform: scale(.94); }
          50% { opacity: .48; transform: scale(1.12); }
        }
        .neo-label { font-size: 11px; font-weight: 700; letter-spacing: .12em; color: var(--neo-muted); text-transform: uppercase; margin-bottom: 8px; }
        .neo-name { margin: 0 0 14px; color: var(--neo-heading); font-size: clamp(34px, 3.1vw, 44px); line-height: 1.08; font-weight: 800; letter-spacing: 0; }
        .neo-meta { display: flex; align-items: center; gap: 22px; color: var(--neo-soft); font-size: 13px; flex-wrap: wrap; }
        .neo-meta span { display: inline-flex; align-items: center; gap: 6px; }
        .neo-meta svg { width: 15px; height: 15px; opacity: .82; }
        .neo-star-text { color: #fbbf24; font-weight: 700; }
        .neo-header-right { display: flex; flex-direction: column; align-items: flex-end; gap: 14px; padding-top: 28px; }
        .neo-edit { border: none; border-radius: 999px; padding: 10px 22px; background: linear-gradient(180deg, #8b5cf6, #7c3aed); color: #fff; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 4px 14px rgba(139,92,246,.35); transition: transform .15s ease, box-shadow .15s ease; }
        .neo-edit:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(139,92,246,.45); }
        .neo-profile-info { min-width: 0; }
        .neo-header-mobile-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 2px;
        }
        .neo-header-mobile-top .neo-label { margin-bottom: 0; }
        button.neo-balance {
          display: inline-flex;
          align-items: baseline;
          gap: 6px;
          color: var(--neo-heading);
          font-family: inherit;
          font-size: 15px;
          font-weight: 600;
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          flex-shrink: 0;
          border-radius: 0;
          box-shadow: none;
          -webkit-appearance: none;
          appearance: none;
          font-variant-numeric: tabular-nums;
          line-height: 1.1;
          letter-spacing: 0.1px;
        }
        button.neo-balance:hover { opacity: 0.85; }
        button.neo-balance:focus-visible { outline: 2px solid rgba(167, 139, 250, 0.55); outline-offset: 3px; }
        button.neo-balance .neo-balance-label { color: ${isDark ? "rgba(255, 255, 255, 0.7)" : LH_PROFILE.balanceLabel}; font-weight: 600; }
        button.neo-balance .neo-balance-dot { color: ${isDark ? "rgba(255, 255, 255, 0.45)" : LH_PROFILE.balanceDot}; font-weight: 700; }
        button.neo-balance .neo-balance-amount { color: var(--neo-heading); font-weight: 700; }
        button.neo-balance::-moz-focus-inner {
          border: 0;
          padding: 0;
        }
        button.neo-balance:focus-visible {
          outline: 2px solid rgba(167, 139, 250, 0.55);
          outline-offset: 3px;
        }
        .neo-balance-group {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        button.neo-balance-add {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 999px;
          border: 1px solid var(--neo-accent-line);
          background: var(--neo-accent-soft);
          color: var(--neo-accent);
          font: inherit;
          font-size: 17px;
          font-weight: 800;
          line-height: 1;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          box-shadow: none;
          -webkit-appearance: none;
          appearance: none;
        }
        button.neo-balance-add:hover { opacity: 0.88; }
        button.neo-balance-add:focus-visible {
          outline: 2px solid rgba(167, 139, 250, 0.55);
          outline-offset: 3px;
        }
        .neo-header-qr-button { display: none; }
        .neo-header-qr-frame {
          width: 100%;
          height: 100%;
          border-radius: 8px;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }
        .neo-header-qr-frame img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }
        .neo-header-qr-state,
        .neo-header-qr-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #667eea;
          font-size: 12px;
          font-weight: 900;
        }
        .neo-header-qr-loading {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid rgba(102,126,234,.24);
          border-top-color: #667eea;
          animation: neoSpin 1s linear infinite;
        }
        .neo-qr-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 10020;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          background: rgba(7, 10, 31, 0.72);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
        .neo-qr-modal-card {
          width: min(100%, 340px);
          border-radius: 16px;
          padding: 16px 18px 18px;
          color: #ffffff;
          background:
            radial-gradient(circle at 88% 18%, rgba(91, 91, 245, .36), transparent 34%),
            radial-gradient(circle at 16% 100%, rgba(124, 58, 237, .24), transparent 42%),
            linear-gradient(145deg, #292a69 0%, #17184a 100%);
          border: 1px solid rgba(196, 181, 253, 0.24);
          box-shadow: 0 26px 80px rgba(0, 0, 0, 0.48), inset 0 1px 0 rgba(255, 255, 255, 0.08);
        }
        .neo-qr-modal-head {
          display: grid;
          grid-template-columns: 34px minmax(0, 1fr) 34px;
          align-items: center;
          gap: 6px;
          margin-bottom: 14px;
        }
        .neo-qr-modal-title {
          text-align: center;
          font-size: 13px;
          line-height: 1.2;
          font-weight: 800;
          letter-spacing: .01em;
          color: rgba(255, 255, 255, .92);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .neo-qr-modal-icon {
          width: 34px;
          height: 34px;
          border: none;
          border-radius: 10px;
          background: transparent;
          color: rgba(255, 255, 255, .72);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
        }
        .neo-qr-modal-icon svg {
          width: 18px;
          height: 18px;
        }
        .neo-qr-modal-icon:hover,
        .neo-qr-modal-icon:focus-visible {
          color: #ffffff;
          background: rgba(255, 255, 255, .08);
          outline: none;
        }
        .neo-qr-modal-box {
          width: min(68vw, 236px);
          aspect-ratio: 1;
          margin: 0 auto 14px;
          border-radius: 9px;
          padding: 8px;
          background: #ffffff;
          box-shadow: 0 18px 42px rgba(8, 9, 36, .38), 0 0 0 1px rgba(255,255,255,.16);
        }
        .neo-qr-modal-box img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
          border-radius: 4px;
        }
        .neo-qr-modal-box img.admin-clickable {
          cursor: pointer;
        }
        .neo-qr-modal-text {
          margin: 0 0 16px;
          text-align: center;
          color: rgba(255, 255, 255, .84);
          font-size: 11.5px;
          line-height: 1.45;
        }
        .neo-qr-modal-actions {
          justify-content: center;
          gap: 10px;
        }
        .neo-qr-modal-actions button {
          color: rgba(255, 255, 255, .9);
          border-color: rgba(255, 255, 255, .10);
          background: rgba(255, 255, 255, .045);
        }
        .neo-header-about--fullrow {
          grid-column: 1 / -1;
          width: 100%;
          align-self: stretch;
          box-sizing: border-box;
        }
        .neo-header-about {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--neo-line);
          min-width: 0;
        }
        .neo-header-about-label {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--neo-muted);
          margin-bottom: 6px;
        }
        .neo-header-about-text {
          margin: 0 0 10px;
          color: var(--neo-text);
          font-size: 13px;
          font-weight: 600;
          line-height: 1.5;
          white-space: pre-wrap;
        }
        .neo-header-about-placeholder {
          margin: 0 0 10px;
          color: var(--neo-muted);
          font-size: 13px;
          line-height: 1.45;
        }
        .neo-header-about-edit { display: inline-block; margin-top: 2px; }
        .neo-banner { display: flex; align-items: center; justify-content: space-between; gap: 16px; min-height: 42px; padding: 10px 18px; border-radius: 12px; margin-bottom: 22px; background: ${isDark ? "rgba(245, 185, 66, .08)" : LH_PROFILE.warningBg}; border: 1px solid ${isDark ? "rgba(245, 185, 66, .34)" : LH_PROFILE.warningBorder}; color: ${isDark ? "#ffc400" : LH_PROFILE.warningText}; font-size: 13.5px; font-weight: 650; }
        .neo-banner-chip { display: inline-flex; width: auto; align-self: flex-start; margin-bottom: 10px; padding: 0 14px; min-height: 0; height: 34px; font-size: 12.5px; box-sizing: border-box; }
        @keyframes neoBannerFade {
          0% { opacity: 0; transform: translateY(2px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .neo-banner-fade { animation: neoBannerFade 0.22s ease both; }
        .neo-banner-row { transition: gap 0.2s ease; }
        .neo-banner-row { display: flex; align-items: center; gap: 10px; margin-bottom: 22px; flex-wrap: wrap; }
        .neo-banner-row .neo-banner { margin-bottom: 0; padding: 0 14px; min-height: 0; height: 34px; font-size: 12.5px; box-sizing: border-box; }
        .neo-banner-email { flex: 1 1 auto; min-width: 0; }
        .neo-banner-verify { flex: 0 0 auto; align-self: center; }
        .neo-banner-close { flex: 0 0 auto; align-self: center; background: rgba(220, 53, 69, .10); border-color: rgba(220, 53, 69, .45); color: #ff6b6b; }
        .neo-banner-close .neo-icon-action { color: #ff6b6b; font-size: 18px; line-height: 1; padding: 0; width: auto; height: auto; border: none; border-radius: 0; background: transparent; }
        .neo-banner-row .neo-banner-action { font-size: 12.5px; line-height: 1; padding: 0; }
        .neo-banner-row .neo-banner-input { height: 22px; padding: 0 8px; font-size: 12.5px; border-radius: 6px; }
        .neo-banner-left { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
        .neo-banner-left svg { width: 17px; height: 17px; flex-shrink: 0; }
        .neo-banner-action, .neo-icon-action { border: none; background: transparent; color: ${isDark ? "#ffc400" : LH_PROFILE.warningText}; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; }
        .neo-banner-action:disabled { opacity: .45; cursor: not-allowed; }
        .neo-banner-actions { display: flex; align-items: center; gap: 8px; }
        .neo-icon-action { width: 26px; height: 26px; border-radius: 50%; border: 1px solid ${isDark ? "rgba(245,185,66,.26)" : LH_PROFILE.warningBorder}; }
        .neo-banner-input { width: min(220px, 48vw); border: 1px solid ${isDark ? "rgba(245,185,66,.36)" : LH_PROFILE.warningBorder}; background: ${isDark ? "rgba(5,13,31,.42)" : LH_PROFILE.bannerInputBg}; color: var(--neo-heading); border-radius: 8px; padding: 7px 10px; outline: none; font: inherit; }
        .neo-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; margin-bottom: 22px; }
        .neo-stat { text-align: left; border: 1px solid var(--neo-line); border-radius: 14px; padding: 20px 22px; min-height: 132px; color: inherit; background: var(--neo-card); box-shadow: var(--neo-shadow); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); font: inherit; cursor: default; }
        button.neo-stat { cursor: pointer; }
        button.neo-stat:hover { border-color: ${isDark ? "rgba(103,232,249,.28)" : LH_PROFILE.statHoverBorder}; background: ${isDark ? "rgba(18,31,68,.31)" : LH_PROFILE.statHoverBg}; }
        .neo-stat-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .neo-stat-title { display: inline-flex; align-items: center; gap: 10px; font-size: 14px; color: var(--neo-text); font-weight: 600; }
        .neo-stat-title svg, .neo-stat-tail svg { width: 19px; height: 19px; color: #22d3ee; }
        .neo-stat-value { color: var(--neo-heading); font-size: 36px; line-height: 1; font-weight: 800; margin-bottom: 7px; }
        .neo-stat-sub { color: var(--neo-soft); font-size: 13px; }
        .neo-grid-middle { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin-bottom: 22px; align-items: stretch; }
        .neo-card { border: 1px solid var(--neo-line); border-radius: 14px; padding: 22px 24px; background: var(--neo-card); box-shadow: var(--neo-shadow); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); min-width: 0; }
        .neo-card-label { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; color: var(--neo-muted); font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
        .neo-full-row { margin-bottom: 22px; }
        .neo-qr-card { min-height: 202px; }
        .neo-about-card { display: flex; flex-direction: column; }
        .neo-matches-panel { min-height: 548px; display: flex; flex-direction: column; }
        .neo-matches-panel,
        .neo-invited-card {
          scroll-margin-top: 24px;
        }
        .neo-matches-panel .neo-card-label { margin-bottom: 10px; }
        .neo-match-tabs { display: inline-flex; align-items: center; align-self: flex-start; gap: 3px; border-radius: 999px; border: 1px solid var(--neo-line); background: var(--neo-card-nested); padding: 3px; margin-bottom: 12px; }
        .neo-match-tab { border: none; background: transparent; color: var(--neo-soft); border-radius: 999px; font: inherit; font-size: 11.5px; font-weight: 800; padding: 7px 14px; cursor: pointer; white-space: nowrap; }
        .neo-match-tab.active { color: ${isDark ? "#fff" : LH_PROFILE.matchTabText}; background: ${isDark ? "rgba(139,92,246,.38)" : LH_PROFILE.matchTabActive}; box-shadow: 0 0 0 1px var(--neo-accent-line) inset; }
        .neo-see { border: none; background: transparent; color: var(--neo-accent); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; letter-spacing: 0; text-transform: none; white-space: nowrap; }
        .neo-see:disabled, .neo-see[aria-disabled="true"] { color: rgba(168, 85, 247, .35); cursor: not-allowed; pointer-events: none; }
        .neo-about-text { margin: 0 0 18px; color: var(--neo-text); font-size: 14px; font-weight: 600; line-height: 1.56; white-space: pre-wrap; }
        .neo-tags { display: flex; flex-wrap: wrap; gap: 8px; }
        .neo-tag { display: inline-flex; align-items: center; min-height: 32px; padding: 6px 14px; border-radius: 999px; border: 1px solid rgba(139,92,246,.3); background: rgba(139,92,246,.15); color: #a855f7; font-size: 12.5px; font-weight: 700; }
        .neo-tag.green { border-color: rgba(34,197,94,.32); background: rgba(34,197,94,.12); color: #22c55e; }
        .neo-matches-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
        .neo-match-card { border: 1px solid var(--neo-line-strong); border-radius: 12px; padding: 16px; background: var(--neo-card-nested); min-width: 0; }
        .neo-match-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; min-width: 0; }
        .neo-match-avatar, .neo-user-avatar { flex-shrink: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 800; }
        .neo-match-avatar { width: 42px; height: 42px; font-size: 13px; }
        .neo-match-copy { min-width: 0; }
        .neo-match-name { color: var(--neo-heading); font-size: 14.5px; font-weight: 800; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .neo-match-role { color: var(--neo-soft); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .neo-match-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; color: var(--neo-soft); font-size: 12.5px; margin-bottom: 12px; }
        .neo-match-meta strong { color: #22c55e; font-weight: 800; }
        .neo-match-button { width: 100%; border-radius: 999px; padding: 9px 0; border: 1px solid var(--neo-accent-line); background: ${isDark ? "transparent" : LH_PROFILE.matchButtonBg}; color: var(--neo-accent); font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; }
        .neo-request-empty .neo-match-button { width: min(420px, 80%); margin: 0 auto; display: block; }
        /* Web: fixed shell ~ header + match card + actions; long copy scrolls inside .neo-request-match-reason */
        .neo-request-shell { height: 432px; flex-shrink: 0; display: flex; flex-direction: column; gap: 10px; }
        .neo-request-card { position: relative; border: 1px solid var(--neo-line); border-radius: 12px; background: var(--neo-card-nested); padding: 14px; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; overflow: hidden; touch-action: pan-y; }
        @keyframes neoSlideFromRight {
          0%   { transform: translateX(10%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes neoSlideFromLeft {
          0%   { transform: translateX(-10%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .neo-request-card.slide-from-right { animation: neoSlideFromRight 0.26s cubic-bezier(0.2, 0.9, 0.3, 1) both; }
        .neo-request-card.slide-from-left  { animation: neoSlideFromLeft  0.26s cubic-bezier(0.2, 0.9, 0.3, 1) both; }
        .neo-request-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 10px; min-height: 48px; padding-right: 32px; }
        .neo-request-menu-wrap { position: absolute; top: 10px; right: 10px; z-index: 10; }
        .neo-request-dots { width: 32px; height: 32px; padding: 0; border: none; background: transparent; color: var(--neo-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; transition: color 0.15s ease, background 0.15s ease, transform 0.12s ease; -webkit-tap-highlight-color: transparent; font: inherit; }
        .neo-request-dots svg { width: 20px; height: 20px; }
        .neo-request-dots:hover { color: var(--neo-accent); background: var(--neo-accent-soft); }
        .neo-request-dots:active { color: var(--neo-accent); background: var(--neo-accent-soft); transform: scale(0.86); }
        .neo-request-menu-wrap--open .neo-request-dots { color: var(--neo-accent); background: var(--neo-accent-soft); transform: scale(0.92); }
        @keyframes neoDropdownIn { from { opacity: 0; transform: translateY(-6px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .neo-request-dropdown { position: absolute; top: calc(100% + 6px); right: 0; min-width: 172px; background: ${isDark ? "rgba(22,33,62,0.97)" : "rgba(250,247,255,0.97)"}; border: 1px solid var(--neo-line-strong); border-radius: 12px; box-shadow: ${isDark ? "0 12px 40px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.38)" : "0 8px 32px rgba(80,50,160,0.16), 0 2px 8px rgba(0,0,0,0.08)"}; overflow: hidden; animation: neoDropdownIn 0.17s cubic-bezier(0.2, 0.9, 0.3, 1) both; backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); }
        .neo-dropdown-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 14px; border: none; background: transparent; color: var(--neo-text); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; text-align: left; transition: background 0.12s ease, color 0.12s ease, transform 0.1s ease; -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        .neo-dropdown-item svg { width: 15px; height: 15px; flex-shrink: 0; opacity: 0.75; transition: opacity 0.12s ease; }
        .neo-dropdown-item:not(:last-child) { border-bottom: 1px solid var(--neo-line); }
        .neo-dropdown-item:hover { background: var(--neo-accent-soft); color: var(--neo-accent); }
        .neo-dropdown-item:hover svg { opacity: 1; }
        .neo-dropdown-item:active { background: var(--neo-accent-line); transform: scale(0.98); }
        .neo-dropdown-item--delete { color: #dc2626; }
        .neo-dropdown-item--delete:hover { background: rgba(220,38,38,0.09); color: #dc2626; }
        .neo-dropdown-item--delete:active { background: rgba(220,38,38,0.16); }
        .neo-dropdown-item:disabled { opacity: 0.42; cursor: not-allowed; pointer-events: none; }
        .neo-matches-panel .neo-card-label .neo-see { font-size: 14px; padding: 4px 0; }
        .neo-request-title-wrap { min-width: 0; flex: 1; }
        .neo-request-title { color: var(--neo-heading); font-size: 14px; font-weight: 800; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .neo-request-subtitle { display: flex; align-items: center; gap: 8px; margin-top: 6px; min-height: 22px; color: var(--neo-muted); font-size: 12px; }
        .neo-request-subtitle span { display: inline-flex; align-items: center; padding: 3px 8px; border-radius: 999px; border: 1px solid; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; }
        .neo-request-count { flex-shrink: 0; color: #22c55e; font-size: 12px; font-weight: 800; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
        .neo-request-count strong { color: inherit; font-size: inherit; }
        .neo-request-count span { color: var(--neo-muted); border: 1px solid var(--neo-line); border-radius: 999px; padding: 2px 6px; font-size: 10px; }
        .neo-request-match-list { flex: 1 1 0; min-height: 0; display: flex; flex-direction: column; gap: 8px; overflow: hidden; }
        .neo-request-match {
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: 0;
          flex: 1;
          min-height: 0;
          padding: 4px 0;
          border: none;
          border-radius: 0;
          background: transparent;
        }
        .neo-request-match-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          flex-shrink: 0;
          min-width: 0;
        }
        .neo-request-match-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 14px;
          font-weight: 900;
          flex-shrink: 0;
        }
        .neo-request-match-main {
          min-width: 0;
          min-height: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          gap: 0;
        }
        .neo-request-match-top { display: flex; align-items: center; gap: 10px; min-width: 0; flex-shrink: 0; }
        .neo-request-match-name { color: var(--neo-heading); font-size: 16px; font-weight: 800; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .neo-request-match-score { flex-shrink: 0; color: #22c55e; font-size: 13px; font-weight: 900; }
        .neo-request-match-role { color: var(--neo-soft); font-size: 12.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-top: 4px; flex-shrink: 0; }
        .neo-request-match-reason {
          color: var(--neo-soft);
          font-size: 16px;
          line-height: 1.52;
          margin-top: 0;
          display: block;
          white-space: normal;
          overflow-y: auto;
          overflow-x: hidden;
          flex: 1 1 auto;
          min-height: 0;
          overflow-wrap: anywhere;
          word-break: normal;
          -webkit-overflow-scrolling: touch;
          scrollbar-gutter: auto;
          box-sizing: border-box;
          border: 1px solid var(--neo-line-strong);
          border-radius: 10px;
          background: ${isDark ? "rgba(4, 10, 28, 0.42)" : LH_PROFILE.navInputBg};
          padding: 10px 12px;
          transition: box-shadow 0.22s ease;
        }
        .neo-request-match-reason.neo-request-match-reason--fade-bottom:not(.neo-request-match-reason--fade-top) {
          box-shadow: inset 0 -18px 16px -10px var(--neo-scroll-shadow), inset 0 -1px 0 rgba(99, 102, 241, 0.12);
        }
        .neo-request-match-reason.neo-request-match-reason--fade-top:not(.neo-request-match-reason--fade-bottom) {
          box-shadow: inset 0 18px 16px -10px var(--neo-scroll-shadow), inset 0 1px 0 rgba(99, 102, 241, 0.12);
        }
        .neo-request-match-reason.neo-request-match-reason--fade-top.neo-request-match-reason--fade-bottom {
          box-shadow:
            inset 0 16px 14px -10px var(--neo-scroll-shadow),
            inset 0 -16px 14px -10px var(--neo-scroll-shadow),
            inset 0 1px 0 rgba(99, 102, 241, 0.1),
            inset 0 -1px 0 rgba(99, 102, 241, 0.1);
        }
        .neo-request-match-actions { display: flex; align-items: center; justify-content: center; gap: 12px; flex-shrink: 0; padding-top: 4px; }
        .neo-request-match-actions.solo { justify-content: flex-end; padding-top: 0; align-self: flex-start; flex-shrink: 0; }
        .neo-request-match-actions button { border: 1px solid var(--neo-accent-line); background: var(--neo-accent-soft); color: var(--neo-accent); border-radius: 999px; padding: 10px 24px; font: inherit; font-size: 14px; font-weight: 800; cursor: pointer; min-width: 130px; }
        .neo-request-match-actions.solo button { padding: 6px 14px; font-size: 12.5px; min-width: 0; }
        .neo-request-match-actions button.danger { border-color: rgba(248,113,113,.34); background: rgba(248,113,113,.1); color: #fca5a5; }
        .neo-request-match-actions button.accept { border-color: rgba(34,197,94,.46); background: rgba(34,197,94,.13); color: #4ade80; }
        .neo-request-match-actions span { color: var(--neo-muted); font-size: 12px; font-weight: 800; }
        .neo-request-no-matches { flex: 1; min-height: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: var(--neo-soft); font-size: 13px; border-radius: 10px; border: 1px dashed var(--neo-line); text-align: center; }
        .neo-request-no-matches button { border: 1px solid rgba(139,92,246,.42); background: rgba(139,92,246,.13); color: #c084fc; border-radius: 999px; padding: 7px 13px; font: inherit; font-size: 11px; font-weight: 800; cursor: pointer; }
        .neo-request-no-matches button:disabled { opacity: .45; cursor: not-allowed; }
        .neo-request-footer { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 100%; padding-top: 2px; }
        .neo-request-footer-inner { display: inline-flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 10px 14px; }
        .neo-request-footer .neo-request-page-btn { border: 1px solid var(--neo-line-strong); background: var(--neo-card-nested); color: var(--neo-text); border-radius: 8px; min-width: 36px; height: 34px; padding: 0 10px; font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; }
        .neo-request-footer .neo-request-page-btn:disabled { opacity: .38; cursor: not-allowed; }
        .neo-request-footer .neo-request-page-indicator { color: var(--neo-muted); font-size: 12px; font-weight: 800; min-width: 52px; text-align: center; letter-spacing: 0.02em; }
        .neo-request-footer .new-search { color: var(--neo-accent); border: 1px solid var(--neo-accent-line); background: var(--neo-accent-soft); border-radius: 8px; min-height: 34px; padding: 6px 16px; font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
        .neo-request-footer .new-search:disabled { opacity: .38; cursor: not-allowed; }
        .neo-request-empty { min-height: 226px; }
        .neo-empty { min-height: 148px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: var(--neo-soft); text-align: center; }
        .neo-event-create-button {
          width: 100%;
          border-radius: 999px;
          padding: 10px 20px;
          border: 1px solid rgba(165, 243, 252, 0.5);
          background: linear-gradient(135deg, rgba(2, 132, 199, 0.55) 0%, rgba(14, 165, 233, 0.72) 38%, rgba(34, 211, 238, 0.68) 72%, rgba(125, 211, 252, 0.52) 100%);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          color: #f0f9ff;
          font: inherit;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          box-shadow:
            0 6px 22px rgba(34, 211, 238, 0.28),
            inset 0 1px 0 rgba(255, 255, 255, 0.38),
            inset 0 -1px 0 rgba(15, 23, 42, 0.18);
          text-shadow: 0 1px 1px rgba(15, 23, 42, 0.25);
          transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
        }
        .neo-event-create-button:hover {
          transform: translateY(-1px);
          border-color: rgba(224, 242, 254, 0.65);
          box-shadow:
            0 8px 28px rgba(34, 211, 238, 0.38),
            inset 0 1px 0 rgba(255, 255, 255, 0.45),
            inset 0 -1px 0 rgba(15, 23, 42, 0.15);
        }
        .neo-event-create-button:active { transform: translateY(0); }
        .neo-bottom { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(300px, 1fr); gap: 18px; margin-bottom: 22px; align-items: start; }
        .neo-event-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
        .neo-event-form { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .neo-event-form input { width: 150px; border-radius: 999px; border: 1px solid var(--neo-line); background: var(--neo-card-nested); color: var(--neo-heading); outline: none; padding: 8px 14px; font: inherit; font-size: 12px; }
        .neo-event-form button, .neo-logout { border: none; border-radius: 999px; padding: 8px 16px; background: linear-gradient(180deg, #8b5cf6, #7c3aed); color: #fff; font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
        .neo-event-list { display: flex; flex-direction: column; gap: 14px; }
        .neo-events-hint { color: var(--neo-soft); font-size: 13px; text-align: center; padding: 8px 0 2px; }
        .neo-event-cta { display: flex; flex-direction: column; gap: 10px; width: 100%; flex-shrink: 0; }
        .neo-event-item { display: flex; align-items: center; gap: 14px; min-width: 0; }
        .neo-event-icon { width: 46px; height: 46px; border-radius: 10px; border: 1px solid var(--neo-accent-line); background: var(--neo-accent-soft); color: var(--neo-accent); display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 900; flex-shrink: 0; }
        .neo-event-info { border: none; background: transparent; color: inherit; text-align: left; padding: 0; min-width: 0; flex: 1; cursor: pointer; font: inherit; }
        .neo-event-title, .neo-event-date { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .neo-event-title { color: var(--neo-heading); font-size: 13.5px; font-weight: 800; margin-bottom: 4px; }
        .neo-event-date, .neo-event-right { color: var(--neo-soft); font-size: 12px; }
        .neo-event-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .neo-toggle { width: 38px; height: 22px; border-radius: 999px; border: 1px solid var(--neo-line-strong); background: ${isDark ? "#4b5563" : LH_PROFILE.toggleBg}; position: relative; cursor: pointer; transition: background .18s ease; }
        .neo-toggle::after { content: ""; position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; background: #fff; border-radius: 50%; transition: transform .18s ease; }
        .neo-toggle.active { background: #22c55e; }
        .neo-toggle.active::after { transform: translateX(16px); }
        .neo-events-panel .neo-event-list { gap: 12px; }
        .neo-event-history-badge { font-size: 11px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase; color: var(--neo-soft); background: ${isDark ? "rgba(148,163,184,0.18)" : LH_PROFILE.eventHistoryBadge}; border: 1px solid var(--neo-line-strong); border-radius: 999px; padding: 4px 10px; white-space: nowrap; }
        .neo-event-history-divider { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--neo-soft); padding: 8px 2px 2px; border-top: 1px dashed var(--neo-line-strong); margin-top: 4px; }
        .neo-qr-card { display: flex; align-items: center; padding: 22px 24px; }
        .neo-qr-wrap { display: flex; align-items: center; gap: 22px; width: 100%; min-width: 0; }
        .neo-qr-box { width: var(--neo-profile-media-size); height: var(--neo-profile-media-size); flex: 0 0 var(--neo-profile-media-size); border-radius: 9px; background: #ffffff; padding: 2px; display: flex; align-items: center; justify-content: center; position: relative; box-shadow: ${isDark ? "0 10px 24px rgba(0,0,0,.18)" : LH_PROFILE.qrShadow}; }
        .neo-qr-box img { width: calc(var(--neo-profile-media-size) - 4px); height: calc(var(--neo-profile-media-size) - 4px); display: block; object-fit: contain; }
        .neo-qr-admin { position: absolute; right: 2px; bottom: 2px; font-size: 9px; line-height: 1; color: #667eea; font-weight: 800; letter-spacing: .03em; }
        .neo-qr-loading, .neo-qr-error { width: calc(var(--neo-profile-media-size) - 4px); min-height: calc(var(--neo-profile-media-size) - 4px); display: flex; align-items: center; justify-content: center; text-align: center; font-size: 10px; color: #667eea; }
        .neo-qr-loading::before { content: ""; width: 30px; height: 30px; border-radius: 50%; border: 3px solid rgba(102,126,234,.22); border-top-color: #667eea; animation: neoSpin 1s linear infinite; }
        .neo-qr-right { min-width: 0; flex: 1; }
        .neo-qr-title-top { display: none; }
        .neo-qr-title { color: var(--neo-muted); font-size: 11px; font-weight: 900; letter-spacing: .14em; text-transform: uppercase; margin-bottom: 10px; }
        .neo-qr-text { color: var(--neo-text); line-height: 1.5; font-size: 18px; font-weight: 600; margin: 0 0 14px; max-width: 280px; }
        .neo-mini-actions { display: flex; gap: 10px; flex-wrap: wrap; }
        .neo-mini-actions button { display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; border: 1px solid var(--neo-line); background: var(--neo-card-nested); color: var(--neo-text); font: inherit; font-size: 11px; padding: 8px 13px; cursor: pointer; }
        .neo-mini-actions svg { width: 13px; height: 13px; }
        .neo-invited-card { margin-bottom: 28px; }
        .neo-invited-head { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 18px; }
        .neo-tabs { display: inline-flex; align-items: center; gap: 3px; border-radius: 999px; border: 1px solid var(--neo-line); background: var(--neo-card-nested); padding: 3px; }
        .neo-tab { border: none; background: transparent; color: var(--neo-soft); border-radius: 999px; font: inherit; font-size: 11.5px; padding: 6px 13px; cursor: pointer; }
        .neo-tab.active { color: #fff; background: ${isDark ? "rgba(139,92,246,.36)" : LH_PROFILE.matchTabActive}; }
        .neo-users-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px 24px; opacity: 1; transition: opacity .24s ease, transform .24s ease; }
        .neo-users-grid.hidden { opacity: 0; transform: translateY(10px); }
        .neo-user { display: flex; align-items: center; gap: 11px; min-width: 0; border-radius: 12px; background: var(--neo-card-nested); border: 1px solid var(--neo-line); padding: 10px 12px; }
        .neo-user-avatar { width: 32px; height: 32px; font-size: 11px; }
        .neo-user-info { min-width: 0; flex: 1; }
        .neo-user-name, .neo-user-email { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .neo-user-name { color: var(--neo-heading); font-size: 13px; font-weight: 750; }
        .neo-user-email { color: var(--neo-soft); font-size: 11.5px; }
        .neo-user-dot { width: 8px; height: 8px; border-radius: 50%; background: #f5b942; flex-shrink: 0; }
        .neo-user-dot.green { background: #22c55e; }
        .neo-footer { display: flex; justify-content: center; padding-top: 8px; }
        .neo-logout { background: rgba(220,50,50,.12); color: #ff7777; border: 1px solid rgba(255,119,119,.18); }
        @media (max-width: 760px) {
          .neo-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 1100px) {
          .neo-stats, .neo-matches-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .neo-grid-middle, .neo-bottom { grid-template-columns: 1fr; }
          .neo-users-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 760px) {
          .neo-profile { --neo-profile-media-size: 108px; }
          .neo-profile { padding: 18px 12px 34px; }
          .neo-shell { display: flex; flex-direction: column; }
          .neo-header { order: 1; }
          .neo-banner { order: 2; }
          .neo-banner-chip { order: 2; width: 100%; align-self: stretch; justify-content: center; text-align: center; }
          .neo-banner-row { order: 2; flex-direction: row; flex-wrap: nowrap; align-items: center; gap: 8px; }
          .neo-banner-row .neo-banner { justify-content: center; text-align: center; padding: 0 8px; }
          .neo-banner-row .neo-banner-email { flex: 1 1 0; min-width: 0; }
          .neo-banner-row .neo-banner-verify { flex: 0 1 auto; }
          .neo-banner-row .neo-banner-close { flex: 0 0 auto; }
          .neo-banner-email .neo-banner-left { justify-content: center; flex: 1 1 auto; min-width: 0; }
          .neo-banner-row .neo-banner-action { padding: 0; text-align: center; }
          .neo-banner-row .neo-banner-input { width: 100%; min-width: 0; }
          .neo-stats { display: none; }
          .neo-matches-panel { order: 4; }
          .neo-grid-middle { order: 3; }
          .neo-events-panel { order: 5; }
          .neo-invited-card { order: 6; }
          .neo-footer { order: 7; }
          .neo-header {
            grid-template-columns: var(--neo-profile-media-size) minmax(0, 1fr) var(--neo-profile-media-size);
            column-gap: 16px;
            row-gap: 10px;
            align-items: start;
          }
          .neo-avatar-column {
            display: contents;
          }
          .neo-avatar-column .neo-avatar {
            grid-column: 1;
            grid-row: 2;
            justify-self: start;
          }
          .neo-profile-info {
            display: contents;
          }
          .neo-header-mobile-top {
            grid-column: 1;
            grid-row: 1;
            min-width: 0;
          }
          .neo-header-mobile-top .neo-balance {
            display: none;
          }
          .neo-balance-mobile-under-avatar {
            grid-column: 2;
            grid-row: 2;
            align-self: center;
            justify-self: center;
            width: var(--neo-profile-media-size);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 7px;
            text-align: center;
            line-height: 1.12;
          }
          .neo-balance-mobile-row {
            display: inline-flex;
            align-items: baseline;
            justify-content: center;
            gap: 4px;
            max-width: 100%;
            white-space: nowrap;
            font-size: 11.5px;
            border: none;
            background: transparent;
            padding: 0;
            margin: 0;
            cursor: pointer;
            font: inherit;
            color: inherit;
          }
          .neo-balance-mobile-under-avatar .neo-balance-label {
            color: ${isDark ? "rgba(255, 255, 255, 0.7)" : LH_PROFILE.balanceLabel};
            font-weight: 600;
          }
          .neo-balance-mobile-under-avatar .neo-balance-dot {
            color: ${isDark ? "rgba(255, 255, 255, 0.45)" : LH_PROFILE.balanceDot};
            font-weight: 700;
          }
          .neo-balance-mobile-under-avatar .neo-balance-amount {
            color: var(--neo-heading);
            font-weight: 700;
            font-size: 12.5px;
          }
          .neo-balance-deposit-button,
          button.neo-balance-add.neo-balance-add--mobile {
            border: 1px solid var(--neo-accent-line);
            border-radius: 999px;
            background: var(--neo-accent-soft);
            color: var(--neo-accent);
            padding: 0;
            width: 24px;
            height: 24px;
            font: inherit;
            font-size: 16px;
            font-weight: 800;
            line-height: 1;
            cursor: pointer;
            white-space: nowrap;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .neo-balance-deposit-button {
            width: auto;
            height: auto;
            padding: 5px 11px;
            font-size: 11.5px;
          }
          .neo-profile-info .neo-name {
            grid-column: 1 / 3;
            grid-row: 3;
            min-width: 0;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          .neo-profile-info .neo-meta {
            display: contents;
          }
          .neo-profile-info .neo-meta > span {
            grid-column: 1 / 3;
            grid-row: 4;
            min-width: 0;
          }
          .neo-header-qr-button {
            display: flex;
            position: relative;
            grid-column: 3;
            grid-row: 2;
            justify-self: end;
            align-self: start;
            width: var(--neo-profile-media-size);
            height: var(--neo-profile-media-size);
            border-radius: 8px;
            border: none;
            background: #ffffff;
            padding: 2px;
            cursor: pointer;
            box-shadow: none;
            isolation: isolate;
            overflow: visible;
            transform: translateZ(0);
            -webkit-tap-highlight-color: transparent;
          }
          .neo-header-qr-button::before,
          .neo-header-qr-button::after {
            content: "";
            position: absolute;
            inset: -14px;
            border-radius: 50%;
            pointer-events: none;
            z-index: -1;
          }
          .neo-header-qr-button::before {
            background: radial-gradient(circle, rgba(167, 139, 250, .42) 0%, rgba(91, 91, 245, .22) 42%, transparent 70%);
            animation: neoQrAura 2.4s ease-in-out infinite;
          }
          .neo-header-qr-button::after {
            border: 1px solid rgba(167, 139, 250, .62);
            animation: neoQrRipple 2.2s ease-out infinite;
          }
          .neo-header-qr-button:active {
            transform: scale(.96);
          }
          .neo-header-qr-button:focus-visible {
            outline: 2px solid rgba(167, 139, 250, .74);
            outline-offset: 3px;
          }
          .neo-header-qr-button:disabled {
            cursor: wait;
            opacity: .72;
          }
          .neo-avatar { border-radius: 16px; }
          .neo-avatar-symbol { width: 42px; height: 42px; }
          .neo-name { font-size: 28px; margin-bottom: 10px; }
          .neo-meta { gap: 10px; font-size: 12px; margin-bottom: 0; }
          .neo-banner { align-items: flex-start; flex-direction: column; }
          .neo-banner-chip, .neo-banner-row .neo-banner { flex-direction: row; align-items: center; }
          .neo-stats, .neo-matches-grid, .neo-users-grid { grid-template-columns: 1fr; }
          .neo-card, .neo-stat { padding: 18px; }
          .neo-event-head, .neo-invited-head { align-items: stretch; flex-direction: column; }
          .neo-event-form { width: 100%; }
          .neo-event-form input { flex: 1; width: auto; }
          .neo-event-item { align-items: center; gap: 10px; }
          .neo-event-info { flex: 7 1 0%; min-width: 0; }
          .neo-event-right { flex: 3 0 0%; flex-direction: row; justify-content: flex-end; align-items: center; }
          .neo-event-right span { display: none; }
          .neo-request-header { flex-direction: column; }
          .neo-request-count { align-self: flex-start; }
          .neo-request-match {
            gap: 10px;
            min-height: 0;
            padding: 4px 0;
          }
          .neo-request-match-header { gap: 10px; }
          .neo-request-match-avatar { width: 44px; height: 44px; font-size: 13px; }
          .neo-request-match-name { font-size: 15px; }
          .neo-request-match-score { font-size: 12px; }
          .neo-request-match-role { font-size: 12px; }
          .neo-request-match-reason { font-size: 15.5px; line-height: 1.5; margin-top: 0; }
          .neo-request-match-actions button { padding: 12px 24px; font-size: 15px; min-width: 140px; }
          .neo-request-match-actions span { font-size: 11.5px; }
          .neo-request-footer { justify-content: center; padding-top: 4px; }
          .neo-request-shell { height: 560px; min-height: 560px; }
          .neo-matches-panel { min-height: 0; }
          .neo-match-tabs { width: 100%; }
          .neo-match-tab { flex: 1; padding-left: 9px; padding-right: 9px; }
          .neo-request-card { min-height: 0; }
          .neo-request-match-list { flex: 1 1 0; min-height: 0; }
          .neo-qr-card { min-height: auto; }
          .neo-qr-title-top { display: block; text-align: center; margin-bottom: 0; }
          .neo-qr-right .neo-qr-title { display: none; }
          .neo-qr-wrap {
            flex-direction: column;
            align-items: center;
            gap: 18px;
          }
          .neo-qr-wrap .neo-qr-title { text-align: center; margin-bottom: 14px; }
          .neo-qr-box {
            width: var(--neo-profile-media-size) !important;
            height: var(--neo-profile-media-size) !important;
            flex: none !important;
          }
          .neo-qr-box img {
            width: 100% !important;
            height: 100% !important;
          }
          .neo-qr-loading, .neo-qr-error { width: 100%; min-height: 100%; }
          .neo-qr-right {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .neo-qr-text { max-width: 100%; }
          .neo-mini-actions { justify-content: center; }
          .neo-toggle { width: 58px; height: 32px; border-radius: 999px; }
          .neo-toggle::after { width: 24px; height: 24px; top: 3px; left: 3px; }
          .neo-toggle.active::after { transform: translateX(26px); }
        }
        @keyframes neoBgOrb1 { 0% { transform: translate(0,0) scale(1); } 20% { transform: translate(12vw,-8vh) scale(1.06); } 50% { transform: translate(-6vw,18vh) scale(0.94); } 75% { transform: translate(16vw,4vh) scale(1.04); } 100% { transform: translate(0,0) scale(1); } }
        @keyframes neoBgOrb2 { 0% { transform: translate(0,0) scale(1); } 30% { transform: translate(-14vw,10vh) scale(1.08); } 60% { transform: translate(8vw,-12vh) scale(0.92); } 100% { transform: translate(0,0) scale(1); } }
        @keyframes neoBgOrb3 { 0% { transform: translate(0,0) scale(1); } 25% { transform: translate(10vw,12vh) scale(0.96); } 55% { transform: translate(-16vw,-7vh) scale(1.05); } 80% { transform: translate(6vw,18vh) scale(0.97); } 100% { transform: translate(0,0) scale(1); } }
        @keyframes neoBgOrb4 { 0% { transform: translate(0,0) scale(1); } 35% { transform: translate(20vw,-14vh) scale(1.1); } 70% { transform: translate(-10vw,9vh) scale(0.93); } 100% { transform: translate(0,0) scale(1); } }
        @keyframes neoBgOrb5 { 0% { transform: translate(0,0) scale(1); } 20% { transform: translate(-18vw,6vh) scale(0.95); } 50% { transform: translate(12vw,-10vh) scale(1.07); } 75% { transform: translate(-7vw,14vh) scale(0.98); } 100% { transform: translate(0,0) scale(1); } }
        @keyframes neoBgOrb6 { 0% { transform: translate(0,0) scale(1); } 40% { transform: translate(14vw,8vh) scale(1.05); } 70% { transform: translate(-12vw,-16vh) scale(0.94); } 100% { transform: translate(0,0) scale(1); } }
        .neo-bg-orbs { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
        .neo-bg-orb { position: absolute; border-radius: 50%; animation-timing-function: ease-in-out; animation-iteration-count: infinite; will-change: transform; }
        @media (prefers-reduced-motion: reduce) { .neo-bg-orbs { display: none; } }
      ` }} />

      <div className="neo-bg-orbs" aria-hidden="true">
        {bgOrbs.map((orb, i) => (
          <div
            key={i}
            className="neo-bg-orb"
            style={{
              left: `${orb.left}%`,
              top: `${orb.top}%`,
              width: orb.size,
              height: orb.size,
              marginLeft: -orb.size / 2,
              marginTop: -orb.size / 2,
              background: `radial-gradient(circle, ${orb.hue} 0%, transparent 68%)`,
              animationName: `neoBgOrb${orb.path}`,
              animationDuration: `${orb.dur}s`,
              animationDelay: `${orb.delay}s`,
            }}
          />
        ))}
      </div>

      <main className="neo-profile" style={{ zoom: isMobile ? textScale : 1 }}>
        <div className="neo-shell">
          <section className="neo-header">
            <div className="neo-avatar-column">
              {renderDashboardAvatar()}
              {isMobile && (
                <div
                  className="neo-balance-mobile-under-avatar"
                  aria-label={lang === "fr" ? "Solde" : lang === "ru" ? "Баланс" : "Balance"}
                >
                  <button
                    type="button"
                    className="neo-balance-mobile-row"
                    onClick={() => openWallet("overview")}
                    aria-label={lang === "fr" ? "Solde" : lang === "ru" ? "Баланс" : "Balance"}
                  >
                    <span className="neo-balance-label">{lang === "fr" ? "Solde" : lang === "ru" ? "Баланс" : "Balance"}</span>
                    <span className="neo-balance-dot">·</span>
                    <span className="neo-balance-amount">{dashboardBalance}</span>
                  </button>
                  <button
                    type="button"
                    className="neo-balance-add neo-balance-add--mobile"
                    onClick={() => openWallet("topup")}
                    aria-label={lang === "fr" ? "Recharger le solde" : lang === "ru" ? "Пополнить баланс" : "Top up balance"}
                  >
                    +
                  </button>
                </div>
              )}
            </div>
            <div className="neo-profile-info">
              {isMobile ? (
                <div className="neo-header-mobile-top">
                  <div className="neo-label">Profile</div>
                  <button
                    type="button"
                    className="neo-balance"
                    onClick={() => openWallet("overview")}
                    aria-label={lang === "fr" ? "Solde" : lang === "ru" ? "Баланс" : "Balance"}
                  >
                    <span className="neo-balance-label">{lang === "fr" ? "Solde" : lang === "ru" ? "Баланс" : "Balance"}</span>
                    <span className="neo-balance-dot">·</span>
                    <span className="neo-balance-amount">{dashboardBalance}</span>
                  </button>
                </div>
              ) : (
                <div className="neo-label">Profile</div>
              )}
              <h1 className="neo-name">{profileName}</h1>
              <div className="neo-meta">
                {/* <span><NeoIcon name="location" />{dashboardLocation}</span> */}
                <span><NeoIcon name="calendar" />{dashboardJoined}</span>
                {isMobile && (
                  <button
                    type="button"
                    className="neo-header-qr-button"
                    onClick={openInviteQrModal}
                    disabled={qrCodeLoading && !qrCodeDataURL}
                    aria-label={inviteQrTitle}
                  >
                    <span className="neo-header-qr-frame">
                      {qrCodeError && !qrCodeDataURL ? (
                        <span className="neo-header-qr-state">!</span>
                      ) : (qrCodeLoading && !qrCodeDataURL) ? (
                        <span className="neo-header-qr-loading" />
                      ) : qrCodeDataURL ? (
                        <img src={qrCodeDataURL} alt="" aria-hidden="true" />
                      ) : (
                        <span className="neo-header-qr-state">QR</span>
                      )}
                    </span>
                  </button>
                )}
                {/* <span><span className="neo-star-text">★</span>{dashboardRating}</span> */}
              </div>
            </div>
            {isMobile && (
              <div className="neo-header-about neo-header-about--fullrow">
                <div className="neo-header-about-label">
                  {lang === "fr" ? "À propos" : lang === "ru" ? "О себе" : "About"}
                </div>
                {profileAboutText ? (
                  <p className="neo-header-about-text">{profileAboutText}</p>
                ) : (
                  <p className="neo-header-about-placeholder">
                    {lang === "fr" ? "Aucune description" : lang === "ru" ? "Нет описания" : "No description yet."}
                  </p>
                )}
                <button
                  type="button"
                  className="neo-see neo-header-about-edit"
                  onClick={() => navigate("/chat/ai", { state: { prefill: "change_about_me" } })}
                >
                  {lang === "fr" ? "Modifier" : lang === "ru" ? "Изменить" : "Edit"}
                </button>
              </div>
            )}
            {!isMobile && (
              <div className="neo-header-right">
                <div className="neo-balance-group">
                  <button
                    type="button"
                    className="neo-balance"
                    onClick={() => openWallet("overview")}
                    aria-label={lang === "fr" ? "Solde" : lang === "ru" ? "Баланс" : "Balance"}
                  >
                    <span className="neo-balance-label">{lang === "fr" ? "Solde" : lang === "ru" ? "Баланс" : "Balance"}</span>
                    <span className="neo-balance-dot">·</span>
                    <span className="neo-balance-amount">{dashboardBalance}</span>
                  </button>
                  <button
                    type="button"
                    className="neo-balance-add"
                    onClick={() => openWallet("topup")}
                    aria-label={lang === "fr" ? "Recharger le solde" : lang === "ru" ? "Пополнить баланс" : "Top up balance"}
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </section>

          {renderEmailBanner()}

          <section className="neo-stats">
            {statCards.map((card) => {
              const Tag = card.onClick ? "button" : "div";
              return (
                <Tag className="neo-stat" key={card.title} onClick={card.onClick}>
                  <div className="neo-stat-head">
                    <div className="neo-stat-title"><NeoIcon name={card.icon} />{card.title}</div>
                  </div>
                  <div className="neo-stat-value">{card.value}</div>
                  <div className="neo-stat-sub">{card.sub}</div>
                </Tag>
              );
            })}
          </section>

          {!isMobile && (
          <section className="neo-grid-middle">
            <div className="neo-card neo-qr-card">
              <div className="neo-qr-wrap">
                <div className="neo-qr-title neo-qr-title-top">Your invite QR</div>
                <div className="neo-qr-box">
                  {qrCodeError && !qrCodeDataURL ? (
                    <button
                      type="button"
                      className="neo-qr-error"
                      onClick={(e) => {
                        e.stopPropagation();
                        loadPermanentQRCode();
                      }}
                    >
                      Retry
                    </button>
                  ) : (qrCodeLoading || !qrCodeDataURL) ? (
                    <div className="neo-qr-loading" />
                  ) : (
                    <img
                      src={qrCodeDataURL}
                      alt="Personal invite QR"
                      onClick={() => adminRoute && navigate(`/${adminRoute}/login`)}
                      style={{ cursor: adminRoute ? "pointer" : "default" }}
                    />
                  )}
                </div>
                <div className="neo-qr-right">
                  <div className="neo-qr-title">Your invite QR</div>
                  <p className="neo-qr-text">{lang === "fr" ? "Partagez pour inviter des personnes directement dans votre reseau." : "Share to invite people directly to your network."}</p>
                  <div className="neo-mini-actions">
                    <button onClick={copyDashboardInvite}><NeoIcon name="copy" />Copy</button>
                    <button onClick={downloadDashboardQr}><NeoIcon name="download" />Download</button>
                  </div>
                </div>
              </div>
            </div>

              <div className="neo-card neo-about-card">
                <div className="neo-card-label">
                  <span>{lang === "fr" ? "À propos" : lang === "ru" ? "О себе" : "About"}</span>
                  <button
                    type="button"
                    className="neo-see"
                    disabled={!profileAboutText}
                    aria-disabled={!profileAboutText}
                    onClick={() => navigate("/chat/ai", { state: { prefill: "change_about_me" } })}
                  >
                    {lang === "fr" ? "Modifier About →" : lang === "ru" ? "Изменить →" : "Change About →"}
                  </button>
                </div>
                {profileAboutText ? (
                  <p className="neo-about-text">{profileAboutText}</p>
                ) : (
                  <button type="button" className="neo-see" onClick={() => navigate("/chat/ai", { state: { prefill: "change_about_me" } })}>
                    {lang === "fr" ? "+ Ajouter une description" : lang === "ru" ? "+ Добавить описание" : "+ Add description"}
                  </button>
                )}
                {/* Temporarily hidden — pill tags (restore with `dashboardTags` block above)
                <div className="neo-tags">
                  {dashboardTags.map((tag, index) => (
                    <span className={"neo-tag" + (index === dashboardTags.length - 1 ? " green" : "")} key={tag}>{tag}</span>
                  ))}
                </div>
                */}
              </div>
          </section>
          )}

          <section ref={matchesSectionRef} className="neo-card neo-matches-panel neo-full-row">
              <div className="neo-card-label">
                <span>{lang === "fr" ? "Demandes & matchs" : lang === "ru" ? "Запросы и метчи" : "Search requests & matches"}</span>
                {activeMatchesTab === "discovery" ? (
                  <button
                    type="button"
                    className="neo-see"
                    disabled={intuitiveDiscoveryLoading || !profileAboutText}
                    aria-disabled={intuitiveDiscoveryLoading || !profileAboutText}
                    onClick={() => {
                      if (!profileAboutText) {
                        navigate("/chat/ai", { state: { prefill: "change_about_me" } });
                        return;
                      }
                      handleIntuitiveDiscovery();
                    }}
                  >
                    {intuitiveDiscoveryLoading
                      ? (lang === "fr" ? "Recherche..." : lang === "ru" ? "Поиск..." : "Searching...")
                      : (lang === "fr" ? "Lancer discovery →" : lang === "ru" ? "Запустить дискавери →" : "Run discovery →")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="neo-see"
                    disabled={!dashboardCurrentMatchGroup}
                    aria-disabled={!dashboardCurrentMatchGroup}
                    onClick={() => navigate("/chat/ai", { state: { prefill: "new_match" } })}
                  >
                    {lang === "fr" ? "Nouvelle recherche →" : lang === "ru" ? "Новый поиск →" : "New search →"}
                  </button>
                )}
              </div>
              <div className="neo-match-tabs" role="tablist" aria-label="Match source">
                <button
                  type="button"
                  className={"neo-match-tab" + (activeMatchesTab === "request" ? " active" : "")}
                  onClick={() => {
                    setActiveMatchesTab("request");
                    setMatchPage(1);
                  }}
                >
                  {lang === "fr" ? "Par demande" : lang === "ru" ? "По запросу" : "Match by demand"}
                </button>
                <button
                  type="button"
                  className={"neo-match-tab" + (activeMatchesTab === "discovery" ? " active" : "")}
                  onClick={() => {
                    setActiveMatchesTab("discovery");
                    setMatchPage(1);
                  }}
                >
                  {lang === "fr" ? "Discovery intuitive" : lang === "ru" ? "Интуитивный поиск" : "Intuitive discovery"}
                </button>
              </div>
              {renderDashboardMatchRequests()}
          </section>

          <section className="neo-card neo-events-panel neo-full-row">
              <div className="neo-event-head">
                <div className="neo-card-label" style={{ marginBottom: 0 }}>Event matching</div>
                <div className="neo-event-form">
                  <input
                    value={eventInviteCode}
                    onChange={(e) => setEventInviteCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") joinEventByInvite();
                    }}
                    placeholder={lang === "fr" ? "Code invitation" : "Event invite code"}
                  />
                  <button onClick={joinEventByInvite}>{lang === "fr" ? "Lier" : "Link"}</button>
                </div>
              </div>
              {eventError && <div style={{ color: "#fca5a5", fontSize: 13, marginBottom: 12 }}>{eventError}</div>}
              <div className="neo-event-list">
                {eventRegistrations.length > 0 ? (
                  (() => {
                    const activeEvents = eventRegistrations.filter((e) => !isEventMatchingExpired(e));
                    const historyEvents = eventRegistrations.filter((e) => isEventMatchingExpired(e));
                    return (
                      <>
                        {activeEvents.length > 0 ? (
                          activeEvents.slice(0, 3).map(renderEventItem)
                        ) : (
                          <div className="neo-events-hint">
                            <span>{lang === "fr" ? "Aucun événement actif" : lang === "ru" ? "Нет активных ивентов" : "No active events"}</span>
                          </div>
                        )}
                        {historyEvents.length > 0 && (
                          <>
                            <div className="neo-event-history-divider">
                              {lang === "fr" ? "Historique" : lang === "ru" ? "История" : "History"}
                            </div>
                            {historyEvents.slice(0, 3).map(renderEventItem)}
                          </>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <div className="neo-events-hint">
                    <span>{lang === "fr" ? "Aucun événement lié" : "No linked events"}</span>
                  </div>
                )}
                <div className="neo-event-cta">
                  <button className="neo-match-button" type="button" onClick={() => { window.location.href = "/events"; }}>
                    {lang === "fr" ? "Voir les événements" : "Browse events"}
                  </button>
                  <button
                    type="button"
                    className="neo-event-create-button"
                    onClick={() => navigate("/events/create")}
                  >
                    {lang === "fr" ? "Créer un événement" : lang === "ru" ? "Создать событие" : "Create events"}
                  </button>
                </div>
              </div>
          </section>

          <section ref={invitedSectionRef} className="neo-card neo-invited-card neo-full-row">
            <div className="neo-invited-head">
              <div className="neo-card-label" style={{ marginBottom: 0 }}>Invited users В· {invitedUsers.length}</div>
              <div className="neo-tabs">
                {[
                  ["all", "All", periodCounts.all],
                  ["week", "Week", periodCounts.week],
                  ["month", "Month", periodCounts.month],
                  ["year", "Year", periodCounts.year],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={"neo-tab" + (timeFilter === key ? " active" : "")}
                    onClick={() => setTimeFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {filteredInvitedUsers.length > 0 ? (
              <div className={"neo-users-grid" + (isListVisible ? "" : " hidden")}>
                {filteredInvitedUsers.map((user) => {
                  const userName = user.name || user.username || user.email || "User";
                  return (
                    <div className="neo-user" key={user.id || user.email || userName}>
                      <div className="neo-user-avatar" style={{ background: getAvatarGradient(userName) }}>{getInitials(userName)}</div>
                      <div className="neo-user-info">
                        <div className="neo-user-name">{userName}</div>
                        <div className="neo-user-email">{user.email || formatDate(user.created_at)}</div>
                      </div>
                      <div className={"neo-user-dot" + (user.profile_completed ? " green" : "")} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="neo-empty">
                <span>{timeFilter === "all" ? (lang === "fr" ? "Aucun utilisateur invitГ©" : "No invited users yet") : (lang === "fr" ? "Aucun invitГ© dans cette pГ©riode" : "No invites in this period")}</span>
              </div>
            )}
          </section>

          <div className="neo-footer">
            <button className="neo-logout" onClick={handleLogoutClick}>{translations[lang]?.logout || "Logout"}</button>
          </div>
        </div>
      </main>

      <AnimatePresence>
        {isMobile && inviteQrModalOpen && qrCodeDataURL && (
          <motion.div
            className="neo-qr-modal-backdrop"
            onClick={() => setInviteQrModalOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <motion.div
              className="neo-qr-modal-card"
              role="dialog"
              aria-modal="true"
              aria-label={inviteQrTitle}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.72, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: 12 }}
              transition={{ type: "spring", stiffness: 360, damping: 30, mass: 0.8 }}
            >
              <div className="neo-qr-modal-head">
                <button
                  type="button"
                  className="neo-qr-modal-icon"
                  onClick={() => setInviteQrModalOpen(false)}
                  aria-label="Back"
                >
                  <NeoIcon name="chevron-left" />
                </button>
                <div className="neo-qr-modal-title">{inviteQrTitle}</div>
                <button
                  type="button"
                  className="neo-qr-modal-icon"
                  onClick={() => setInviteQrModalOpen(false)}
                  aria-label="Close"
                >
                  <NeoIcon name="x" />
                </button>
              </div>

              <div className="neo-qr-modal-box">
                <img
                  src={qrCodeDataURL}
                  alt="Personal invite QR"
                  className={adminRoute ? "admin-clickable" : ""}
                  onClick={adminRoute ? handleInviteQrModalImageClick : undefined}
                />
              </div>

              <p className="neo-qr-modal-text">{inviteQrShareText}</p>

              <div className="neo-mini-actions neo-qr-modal-actions">
                <button type="button" onClick={copyDashboardInvite}><NeoIcon name="copy" />Copy</button>
                <button type="button" onClick={downloadDashboardQr}><NeoIcon name="download" />Download</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Reject Modal ─────────────────────────────────────────────────── */}
      {rejectModal.isOpen && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease-out forwards",
            padding: "0 16px",
            boxSizing: "border-box",
          }}
          onClick={closeRejectModal}
        >
          <div
            style={{
              background: themeVars.card,
              borderRadius: 16,
              padding: isMobile ? "24px 20px" : "28px",
              width: "100%",
              maxWidth: 460,
              boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
              animation: "modalPopIn 0.35s ease-out forwards",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: 22, color: themeVars.fg, textAlign: "center" }}>
              {lang === "fr" ? "Refuser le match" : "Decline Match"}
            </h3>
            <textarea
              placeholder={
                lang === "fr"
                  ? "Pourquoi refusez-vous ce match ? (obligatoire)"
                  : "Why are you declining this match? (required)"
              }
              value={rejectModal.reason}
              onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
              style={{
                width: "100%", minHeight: 120,
                padding: 14, borderRadius: 10,
                border: `1px solid ${themeVars.border}`,
                background: themeVars.bg, color: themeVars.fg,
                fontSize: 15, resize: "vertical",
                marginBottom: 24, fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 14 }}>
              <button
                onClick={handleRejectWithReason}
                disabled={!rejectModal.reason.trim()}
                style={{
                  flex: 1,
                  background: rejectModal.reason.trim() ? "#dc3545" : "#888",
                  color: "#fff", border: "none", borderRadius: 10,
                  padding: "14px", fontWeight: 600, fontSize: 16,
                  cursor: rejectModal.reason.trim() ? "pointer" : "not-allowed",
                  opacity: rejectModal.reason.trim() ? 1 : 0.6,
                  transition: "all 0.2s",
                }}
              >
                {lang === "fr" ? "Refuser" : "Decline"}
              </button>
              <button
                onClick={closeRejectModal}
                style={{
                  flex: 1,
                  background: "transparent", color: themeVars.fg,
                  border: `1px solid ${themeVars.border}`,
                  borderRadius: 10, padding: "14px",
                  fontWeight: 600, fontSize: 16, cursor: "pointer",
                }}
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <WalletModal
        isOpen={walletOpen}
        onClose={() => setWalletOpen(false)}
        token={token}
        lang={lang}
        isDark={isDark}
        initialTab={walletInitialTab}
        onBalanceUpdated={handleWalletBalanceUpdated}
      />
    </>
  );
}
